/**
 * Family Tree detail: header, member list, CSS-based generational tree view,
 * events tab. Admins can add members, link relationships, create events.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { EVENT_CATEGORIES, categoriesWithEvents, getEventMeta } from "@/lib/eventCategories";
import { Plus, Users, Calendar, Settings, Flower2, ExternalLink, Trash2, Link2, Image as ImageIcon, MessageCircle, Send, X, Share2, Download, Upload, MessagesSquare, Copy, Clock, ShieldCheck, UserPlus, Pencil, ChevronDown, ChevronUp, UserCircle2, Loader2, BookOpen, MapPin } from "lucide-react";
import { API } from "@/lib/api";
import FamilyChat from "@/components/FamilyChat";
import FamilyMemberContactModal from "@/components/FamilyMemberContactModal";
import TranslatableInput from "@/components/TranslatableInput";
import AIImproveButton from "@/components/AIImproveButton";
import AncestorAvatar from "@/components/AncestorAvatar";
import CopyLinkIcon from "@/components/CopyLinkIcon";
import VisibilityToggle from "@/components/VisibilityToggle";
import FamilyLegacyScoreCard from "@/components/FamilyLegacyScoreCard";
import { pickI18n } from "@/lib/i18n";
import { useLocality } from "@/contexts/LocalityContext";

export default function FamilyTreeDetail() {
  const { treeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tree, setTree] = useState(null);
  const [members, setMembers] = useState([]);
  const [rels, setRels] = useState([]);
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState("tree");
  const [memberModal, setMemberModal] = useState(false);
  const [relModal, setRelModal] = useState(false);
  const [eventModal, setEventModal] = useState(false);
  const [shareModal, setShareModal] = useState(false);
  const [gedcomModal, setGedcomModal] = useState(false);
  const [editTreeModal, setEditTreeModal] = useState(false);
  const [error, setError] = useState(null);
  // Public "share" card used when the current user can't view the tree
  // (private/invite tree + non-member). Fetched lazily on error.
  const [publicCard, setPublicCard] = useState(null);

  const isAdmin = tree && user && (tree.owner_id === user.id || (tree.admin_ids || []).includes(user.id) || user.is_admin);

  const load = async () => {
    try {
      const t = await http.get(`/family-trees/${treeId}`);
      // Follow the seed-set redirect for soft-deleted legacy trees.
      if (t.data?.deleted && t.data?.redirect_to_slug && t.data.redirect_to_slug !== treeId) {
        navigate(`/family/${t.data.redirect_to_slug}`, { replace: true });
        return;
      }
      setTree(t.data);
      const m = await http.get(`/family-trees/${t.data.id}/members`);
      setMembers(m.data.items);
      const r = await http.get(`/family-trees/${t.data.id}/relationships`);
      setRels(r.data.items);
      const e = await http.get(`/family-trees/${t.data.id}/events`);
      setEvents(e.data.items);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Cannot load tree";
      setError(detail);
      // If it was a 403 (invite/private tree, non-member), try the public
      // /share endpoint so we can still show a card + a "Request Access"
      // tab. 404s mean the tree really doesn't exist.
      if (err?.response?.status === 403) {
        try {
          const s = await http.get(`/family-trees/${treeId}/share`);
          setPublicCard(s.data);
        } catch { /* private trees return 404 on /share too */ }
      }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [treeId]);

  if (error) {
    // Prefer the public /share card when available (invite trees). If the
    // tree is fully private OR truly missing, fall back to the simple
    // message.
    if (publicCard) {
      return <RequestAccessLanding card={publicCard} user={user} />;
    }
    return <div className="max-w-3xl mx-auto py-16 px-6 text-center">
      <h2 className="font-display text-2xl">{error}</h2>
    </div>;
  }
  if (!tree) return <div className="max-w-3xl mx-auto py-16 px-6 text-center text-sm">Loading tree…</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="family-tree-page">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <Link to="/family" className="text-xs underline" style={{ color: "var(--wofy-primary)" }}>← Back to family trees</Link>
          <h1 className="font-display text-4xl md:text-5xl leading-tight mt-2">{tree.name}</h1>
          <div className="mt-2 text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
            {tree.country || "Global"} · {tree.member_count} member{tree.member_count === 1 ? "" : "s"} · {tree.visibility}
          </div>
          {tree.description && <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--wofy-text-soft)" }}>{tree.description}</p>}
          <VisibilityToggle
            kind="family-tree"
            currentValue={tree.visibility || "public"}
            canManage={!!isAdmin}
            isSignedIn={!!user}
            targetId={tree.id}
            onSaved={(nv) => setTree((prev) => prev ? { ...prev, visibility: nv } : prev)}
          />
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setEditTreeModal(true)} data-testid="ft-edit-tree" className="wofy-btn-ghost">
              <Pencil className="w-4 h-4" /> Edit tree
            </button>
            <button onClick={() => setMemberModal(true)} data-testid="ft-add-member" className="wofy-btn-primary">
              <Plus className="w-4 h-4" /> Add member
            </button>
            <button onClick={() => setRelModal(true)} data-testid="ft-add-rel" className="wofy-btn-ghost" disabled={members.length < 2}>
              <Link2 className="w-4 h-4" /> Link relationship
            </button>
            <button onClick={() => setEventModal(true)} data-testid="ft-add-event" className="wofy-btn-ghost">
              <Calendar className="w-4 h-4" /> New event
            </button>
            <button onClick={() => setGedcomModal(true)} data-testid="ft-gedcom" className="wofy-btn-ghost">
              <Download className="w-4 h-4" /> GEDCOM
            </button>
          </div>
        )}
        <div className="flex gap-2 flex-wrap w-full mt-2 md:w-auto md:mt-0">
          <Link
            to={`/family/${tree.id}/manage`}
            data-testid="ft-manage"
            className="wofy-btn-primary"
          >
            <ShieldCheck className="w-4 h-4" /> Family Management Center
          </Link>
          <Link
            to={`/family/${tree.id}/photos-batch`}
            data-testid="ft-photos-batch"
            className="wofy-btn-ghost"
          >
            <ImageIcon className="w-4 h-4" /> Photos batch
          </Link>
          <button onClick={() => setShareModal(true)} data-testid="ft-share" className="wofy-btn-ghost">
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>
      </div>

      <FamilyLegacyScoreCard treeId={tree.id} />

      <div className="flex gap-2 mb-6 border-b overflow-x-auto" style={{ borderColor: "var(--wofy-line)" }}>
        {[
          { v: "tree", label: "Tree View", icon: Users },
          { v: "members", label: "Profile View", icon: UserCircle2 },
          { v: "timeline", label: "Timeline", icon: Clock },
          { v: "events", label: "Events", icon: Calendar },
          { v: "albums", label: "Albums", icon: ImageIcon },
          { v: "documents", label: "Documents", icon: BookOpen },
          { v: "chat", label: "Family Chat", icon: MessagesSquare },
          { v: "invite", label: "Invite", icon: UserPlus },
          ...(isAdmin ? [{ v: "access", label: "Access", icon: Users }] : []),
        ].map(({ v, label, icon: Icon }) => (
          <button
            key={v}
            data-testid={`ft-tab-${v}`}
            onClick={() => setTab(v)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px inline-flex items-center gap-1.5 whitespace-nowrap`}
            style={{ borderColor: tab === v ? "var(--wofy-primary)" : "transparent", color: tab === v ? "var(--wofy-primary)" : "var(--wofy-text-soft)" }}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* Sticky caption / hero header above the active tab's white content
          area. Uses the same tree name shown at the very top so the family
          identity stays visible even when the visitor scrolls into the
          member grid. */}
      {["tree", "members", "timeline", "invite"].includes(tab) && (
        <div className="mb-4" data-testid="ft-content-caption">
          <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
            {tab === "invite" ? "INVITE FAMILY" : `${tab.toUpperCase()} · ${tree.member_count || members.length} MEMBER${(tree.member_count || members.length) === 1 ? "" : "S"}`}
          </div>
          <h2 className="font-display text-2xl md:text-3xl leading-tight" data-testid="ft-content-caption-name">
            {tree.name}
          </h2>
        </div>
      )}

      {tab === "tree" && <TreeView members={members} rels={rels} tree={tree} isAdmin={isAdmin} onChanged={load} />}
      {tab === "timeline" && <FamilyTimeline members={members} rels={rels} events={events} />}
      {tab === "members" && <MembersList members={members} isAdmin={isAdmin} treeId={tree.id} treeSlug={tree.slug || tree.id} tree={tree} onDeleted={load} user={user} />}
      {tab === "events" && <EventsList events={events} treeId={tree.id} onChanged={load} isAdmin={isAdmin} />}
      {tab === "albums" && <AlbumsTab treeId={tree.id} isAdmin={isAdmin} />}
      {tab === "documents" && <DocumentsList treeSlug={tree.slug || tree.id} isAdmin={isAdmin} />}
      {tab === "chat" && (
        <FamilyChat
          treeId={tree.id}
          isMember={isAdmin || (user && (tree.member_user_ids || []).includes(user.id))}
          canModerate={isAdmin}
        />
      )}
      {tab === "invite" && <InviteTab treeId={tree.id} treeName={tree.name} isAdmin={isAdmin} user={user} />}
      {tab === "access" && isAdmin && <AccessTab treeId={tree.id} />}

      {memberModal && <MemberModal treeId={tree.id} onClose={() => setMemberModal(false)} onSaved={load} />}
      {relModal && <RelationshipModal treeId={tree.id} members={members} onClose={() => setRelModal(false)} onSaved={load} />}
      {eventModal && <EventModal treeId={tree.id} onClose={() => setEventModal(false)} onSaved={load} />}
      {shareModal && <ShareModal tree={tree} onClose={() => setShareModal(false)} />}
      {gedcomModal && <GedcomModal tree={tree} isAdmin={isAdmin} onClose={() => setGedcomModal(false)} onImported={load} />}
      {editTreeModal && <EditTreeModal tree={tree} onClose={() => setEditTreeModal(false)} onSaved={(next) => { setTree(next); setEditTreeModal(false); }} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Edit-tree modal — owner/admin can update the tree's name,           */
/* description, visibility, country and alternate/branch labels.       */
/* ------------------------------------------------------------------ */
function EditTreeModal({ tree, onClose, onSaved }) {
  const [name, setName] = useState(tree.name || "");
  const [description, setDescription] = useState(tree.description || "");
  const [visibility, setVisibility] = useState(tree.visibility || "public");
  const [country, setCountry] = useState(tree.country || "");
  const [altNames, setAltNames] = useState((tree.alternate_names || []).join(", "));
  const [branchNames, setBranchNames] = useState((tree.branch_names || []).join(", "));
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Tree name is required."); return; }
    setBusy(true);
    try {
      const { data } = await http.patch(`/family-trees/${tree.id}`, {
        name: name.trim(),
        description: description.trim() || null,
        visibility,
        country: country.trim() || null,
        alternate_names: altNames.split(",").map((s) => s.trim()).filter(Boolean),
        branch_names: branchNames.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Family tree updated.");
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save changes.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose} data-testid="ft-edit-tree-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="wofy-card max-w-lg w-full max-h-[90vh] overflow-y-auto !p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl inline-flex items-center gap-2"><Pencil className="w-4 h-4" /> Edit family tree</h3>
          <button type="button" onClick={onClose} className="opacity-60 hover:opacity-100" aria-label="Close" data-testid="ft-edit-tree-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <label className="block mb-3">
          <span className="text-[11px] font-mono uppercase tracking-wider">Tree name *</span>
          <input required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} className="wofy-input w-full mt-1" data-testid="ft-edit-tree-name" />
        </label>
        <label className="block mb-3">
          <span className="text-[11px] font-mono uppercase tracking-wider">Description</span>
          <textarea rows={3} maxLength={1200} value={description} onChange={(e) => setDescription(e.target.value)} className="wofy-input w-full mt-1" data-testid="ft-edit-tree-description" />
        </label>
        <label className="block mb-3">
          <span className="text-[11px] font-mono uppercase tracking-wider">Country</span>
          <input maxLength={120} value={country} onChange={(e) => setCountry(e.target.value)} className="wofy-input w-full mt-1" data-testid="ft-edit-tree-country" />
        </label>
        <label className="block mb-3">
          <span className="text-[11px] font-mono uppercase tracking-wider">Alternate spellings (comma-separated)</span>
          <input maxLength={400} value={altNames} onChange={(e) => setAltNames(e.target.value)} className="wofy-input w-full mt-1" data-testid="ft-edit-tree-alts" placeholder="Vell, Vellu, वेल" />
        </label>
        <label className="block mb-3">
          <span className="text-[11px] font-mono uppercase tracking-wider">Branch labels (comma-separated)</span>
          <input maxLength={400} value={branchNames} onChange={(e) => setBranchNames(e.target.value)} className="wofy-input w-full mt-1" data-testid="ft-edit-tree-branches" placeholder="Kottayam · Idukki · Munnar" />
        </label>
        <label className="block mb-4">
          <span className="text-[11px] font-mono uppercase tracking-wider">Visibility</span>
          <select value={visibility} onChange={(e) => setVisibility(e.target.value)} className="wofy-input w-full mt-1" data-testid="ft-edit-tree-visibility">
            <option value="public">Public — anyone can view</option>
            <option value="invite">Invite-only — visible but read-only for outsiders</option>
            <option value="private">Private — only members can view</option>
          </select>
        </label>
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="wofy-btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid="ft-edit-tree-save">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Hierarchical family-tree diagram (tidy tree with SVG connectors).   */
/*                                                                     */
/* Layout algorithm — designed to render 100–300 members cleanly:      */
/*   1. Union-find spouse edges → each "family unit" is 1 or 2 people. */
/*   2. Each unit picks ONE "primary parent unit" (the parent-unit of  */
/*      its earliest-born blood-line member). This turns the DAG into  */
/*      a rendering tree, avoiding duplicate placements.               */
/*   3. Bottom-up: compute subtree-width = max(sum(children widths) +  */
/*      sibling gaps, own width).                                      */
/*   4. Top-down: place each root at cursorX; place children left-to-  */
/*      right within the root's subtree; center each parent above its  */
/*      children.                                                      */
/*   5. Render nodes as absolute-positioned cards; draw orthogonal     */
/*      SVG connectors (down-stub → horizontal bus → down-stub) from   */
/*      each parent unit to each child unit.                           */
/*   6. Pan via mouse-drag on the canvas background; zoom via toolbar  */
/*      buttons and Ctrl/⌘ + wheel.                                    */
/* ------------------------------------------------------------------ */
const TREE_CARD_W = 96;
const TREE_CARD_H = 148;
const COUPLE_GAP = 14;      // horizontal gap between the two cards of a couple
const SIBLING_GAP = 22;     // horizontal gap between neighboring units
const GEN_GAP = 84;         // vertical space between rows of a generation
const ROW_H = TREE_CARD_H + GEN_GAP;
const H_PAD = 32;
const V_PAD = 32;

function TreeMiniCard({ m, treeSlug, deceased, isAdmin, onQuickEdit }) {
  const name = [m.first_name, m.last_name].filter(Boolean).join(" ");
  const y1 = (m.birth_date || "").slice(0, 4);
  const y2 = (m.death_date || "").slice(0, 4);
  const yrs = y1 && y2 ? `${y1}–${y2}` : y1 ? `b. ${y1}` : y2 ? `d. ${y2}` : "";
  return (
    <div className="relative" style={{ width: TREE_CARD_W, height: TREE_CARD_H, flex: "none" }}>
      <Link
        to={`/family/${treeSlug}/member/${m.id}`}
        data-testid={`tree-node-${m.id}`}
        onDragStart={(e) => e.preventDefault()}
        className="group flex flex-col items-center text-center rounded-lg border bg-white hover:shadow-md transition-shadow overflow-hidden relative w-full h-full"
        style={{
          borderColor: deceased ? "rgba(107,114,128,0.7)" : "var(--wofy-primary)",
        }}
      >
        <div className="w-full relative overflow-hidden" style={{ height: 78 }}>
          <AncestorAvatar
            member={m}
            size={78}
            showAddChip={false}
            className="!rounded-none w-full h-full"
          />
          <CopyLinkIcon
            url={m.memorial_slug ? `/celebrate/${m.memorial_slug}` : `/family/${treeSlug}/member/${m.id}`}
            position="tr"
            label={m.memorial_slug ? "Copy memorial link" : "Copy profile link"}
            testId={`tree-node-copy-${m.id}`}
          />
        </div>
        <div className="px-1 pt-1 w-full flex-1 flex flex-col justify-start">
          <div
            className="text-[10px] leading-[1.15] font-medium w-full break-words"
            title={name}
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              wordBreak: "break-word",
              hyphens: "auto",
            }}
          >
            {name || "—"}
          </div>
          {m.nickname && (
            <div className="text-[9px] italic leading-tight truncate" style={{ color: "var(--wofy-primary)" }} title={`Nickname: ${m.nickname}`} data-testid={`tree-node-nickname-${m.id}`}>
              "{m.nickname}"
            </div>
          )}
          {yrs && <div className="text-[9px] opacity-60">{yrs}</div>}
          {m.location && !deceased && (
            <div className="text-[9px] opacity-70 leading-tight truncate mt-auto pb-0.5 flex items-center gap-0.5 justify-center" title={`Current place: ${m.location}`} data-testid={`tree-node-location-${m.id}`}>
              <MapPin className="w-2.5 h-2.5 flex-none opacity-60" />
              <span className="truncate">{m.location}</span>
            </div>
          )}
        </div>
      </Link>
      {isAdmin && typeof onQuickEdit === "function" && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onQuickEdit(); }}
          data-testid={`tree-node-quickedit-${m.id}`}
          aria-label="Quick edit"
          title="Quick edit"
          className="absolute top-1 left-1 w-6 h-6 rounded-full bg-white/95 border shadow-sm flex items-center justify-center hover:scale-110 transition"
          style={{ borderColor: "var(--wofy-border)", zIndex: 5 }}
        >
          <Pencil className="w-3 h-3" style={{ color: "var(--wofy-primary)" }} />
        </button>
      )}
    </div>
  );
}

/**
 * OrphanCard — wraps TreeMiniCard for the "Not yet linked" strip and
 * layers on a delete affordance so tree admins can prune unlinked
 * members without leaving the canvas.
 */
function OrphanCard({ m, treeSlug, treeId, isAdmin, onChanged, onQuickEdit }) {
  const [busy, setBusy] = useState(false);
  const del = async (e) => {
    e.preventDefault(); e.stopPropagation();
    const name = [m.first_name, m.last_name].filter(Boolean).join(" ") || "this person";
    if (!window.confirm(`Delete "${name}" permanently from this tree? This can't be undone.`)) return;
    setBusy(true);
    try {
      await http.delete(`/family-trees/${treeId}/members/${m.id}`);
      toast.success(`Removed ${name} from the tree.`);
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="relative" style={{ width: TREE_CARD_W, height: TREE_CARD_H }}>
      <TreeMiniCard m={m} treeSlug={treeSlug} deceased={!!m.death_date} isAdmin={isAdmin} onQuickEdit={onQuickEdit} />
      {isAdmin && (
        <button
          type="button"
          onClick={del}
          disabled={busy}
          data-testid={`orphan-delete-${m.id}`}
          aria-label="Delete unlinked person"
          title="Delete this unlinked person"
          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/95 border shadow-sm flex items-center justify-center hover:scale-110 transition disabled:opacity-40"
          style={{ borderColor: "#E5B4B4", zIndex: 5 }}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" style={{ color: "#B23A3A" }} />}
        </button>
      )}
    </div>
  );
}

/**
 * QuickEditDrawer — right-side edit panel that lets tree admins update
 * the essential member fields (name, nickname, dates, gender, current
 * location, notes, profile photo) without leaving the tree canvas.
 * Uses PATCH /api/family-trees/{tree_id}/members/{mid}.
 */
function QuickEditDrawer({ memberId, tree, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [m, setM] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await http.get(`/family-trees/${tree.id}/members/${memberId}/profile`);
        if (cancelled) return;
        setM(data?.member || data || {});
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Couldn't load member.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [memberId, tree.id]);

  const upd = (k, v) => setM((p) => ({ ...(p || {}), [k]: v }));

  const onPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Profile photo must be under 2 MB.");
    const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
    upd("photo", dataUrl);
  };

  const save = async () => {
    if (!m?.first_name?.trim()) return toast.error("First name is required.");
    setSaving(true);
    try {
      const payload = {
        first_name: m.first_name || "",
        middle_name: m.middle_name || "",
        last_name: m.last_name || "",
        nickname: m.nickname || "",
        gender: m.gender || "",
        birth_date: m.birth_date || "",
        death_date: m.death_date || "",
        location: m.location || "",
        birth_place: m.birth_place || "",
        photo: typeof m.photo === "string" ? m.photo : undefined,
        bio: typeof m.bio === "string" ? m.bio : m.bio || "",
      };
      await http.patch(`/family-trees/${tree.id}/members/${memberId}`, payload);
      toast.success("Saved.");
      if (typeof onSaved === "function") onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110]" data-testid="quick-edit-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="absolute top-0 right-0 h-full bg-white shadow-2xl overflow-y-auto"
        style={{ width: "min(480px, 100vw)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between z-10" style={{ borderColor: "var(--wofy-border)" }}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">Quick edit</div>
            <h2 className="text-lg font-bold" data-testid="quick-edit-title">{loading ? "Loading…" : ([m?.first_name, m?.last_name].filter(Boolean).join(" ") || "New member")}</h2>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="quick-edit-close" className="w-8 h-8 rounded-full border flex items-center justify-center hover:bg-gray-50" style={{ borderColor: "var(--wofy-border)" }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading && (
          <div className="p-6 text-sm opacity-60 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading member…</div>
        )}
        {err && !loading && (
          <div className="p-6 text-sm" style={{ color: "#B23A3A" }}>{err}</div>
        )}

        {!loading && !err && m && (
          <div className="p-5 space-y-4">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider opacity-70">Profile photo</label>
              <div className="mt-1.5 flex items-center gap-3">
                <div className="w-20 h-20 rounded-full overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
                  {m.photo ? (
                    <img src={m.photo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-2xl bg-gray-100">{(m.first_name || "?")[0]}</div>
                  )}
                </div>
                <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-xs hover:bg-gray-50" style={{ borderColor: "var(--wofy-border)" }} data-testid="quick-edit-photo-upload">
                  <Upload className="w-3.5 h-3.5" /> Change photo
                  <input type="file" accept="image/*" onChange={onPhoto} className="hidden" />
                </label>
                {m.photo && (
                  <button type="button" onClick={() => upd("photo", "")} className="text-xs opacity-60 hover:opacity-100">Remove</button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs">
                <span className="font-mono uppercase tracking-wider opacity-70">First name *</span>
                <input value={m.first_name || ""} onChange={(e) => upd("first_name", e.target.value)} data-testid="quick-edit-first-name" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
              </label>
              <label className="block text-xs">
                <span className="font-mono uppercase tracking-wider opacity-70">Last name</span>
                <input value={m.last_name || ""} onChange={(e) => upd("last_name", e.target.value)} data-testid="quick-edit-last-name" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
              </label>
            </div>

            <label className="block text-xs">
              <span className="font-mono uppercase tracking-wider opacity-70">Middle name</span>
              <input value={m.middle_name || ""} onChange={(e) => upd("middle_name", e.target.value)} data-testid="quick-edit-middle-name" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
            </label>

            <label className="block text-xs">
              <span className="font-mono uppercase tracking-wider opacity-70">Nickname / Preferred name</span>
              <input value={m.nickname || ""} onChange={(e) => upd("nickname", e.target.value)} placeholder="e.g. Kutty, Babu, Chinnu" data-testid="quick-edit-nickname" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs">
                <span className="font-mono uppercase tracking-wider opacity-70">Gender</span>
                <select value={m.gender || ""} onChange={(e) => upd("gender", e.target.value)} data-testid="quick-edit-gender" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm bg-white" style={{ borderColor: "var(--wofy-border)" }}>
                  <option value="">—</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label className="block text-xs">
                <span className="font-mono uppercase tracking-wider opacity-70">Birth year / date</span>
                <input value={m.birth_date || ""} onChange={(e) => upd("birth_date", e.target.value)} placeholder="YYYY-MM-DD or YYYY" data-testid="quick-edit-birth" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs">
                <span className="font-mono uppercase tracking-wider opacity-70">Birthplace</span>
                <input value={m.birth_place || ""} onChange={(e) => upd("birth_place", e.target.value)} placeholder="e.g. Kollam" data-testid="quick-edit-birth-place" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
              </label>
              <label className="block text-xs">
                <span className="font-mono uppercase tracking-wider opacity-70">Current place of stay</span>
                <input value={m.location || ""} onChange={(e) => upd("location", e.target.value)} placeholder="e.g. Bengaluru" data-testid="quick-edit-location" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
              </label>
            </div>

            <label className="block text-xs">
              <span className="font-mono uppercase tracking-wider opacity-70">Death date (if applicable)</span>
              <input value={m.death_date || ""} onChange={(e) => upd("death_date", e.target.value)} placeholder="YYYY-MM-DD or YYYY (leave blank if living)" data-testid="quick-edit-death" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
            </label>

            <label className="block text-xs">
              <span className="font-mono uppercase tracking-wider opacity-70">Short bio / notes</span>
              <textarea value={typeof m.bio === "string" ? m.bio : ""} onChange={(e) => upd("bio", e.target.value)} rows={4} data-testid="quick-edit-bio" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
            </label>

            <div className="pt-2 flex items-center justify-between border-t" style={{ borderColor: "var(--wofy-border)" }}>
              <Link
                to={`/family/${tree.slug || tree.id}/member/${memberId}/edit`}
                className="text-xs underline opacity-70 hover:opacity-100"
                data-testid="quick-edit-open-full"
              >
                Open full editor →
              </Link>
              <div className="flex items-center gap-2">
                <button type="button" onClick={onClose} data-testid="quick-edit-cancel" className="px-3 py-1.5 rounded-lg text-xs border" style={{ borderColor: "var(--wofy-border)" }}>Cancel</button>
                <button type="button" onClick={save} disabled={saving} data-testid="quick-edit-save" className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-60" style={{ background: "var(--wofy-primary)" }}>
                  {saving ? <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1" /> : null}
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* Build the tidy layout: returns { units:Map, roots:[id], width, height,
 * orphans:[memberId], orphanUnitIds:Set }.
 * `collapsedIds` (Set<unitId>) tells the layout to hide the descendants
 * of those units — a collapsed unit still gets a position, but its
 * subtree is skipped for width/placement so downstream siblings can
 * reclaim the horizontal real estate.
 */
function buildFamilyLayout(members, rels, collapsedIds) {
  const isCollapsed = (uid) => (collapsedIds ? collapsedIds.has(uid) : false);
  const spouseEdges = rels.filter((r) => r.type === "spouse");
  const parentEdges = rels.filter((r) => r.type === "parent");

  // Union-find over members via spouse edges → couple = 1 unit.
  const parent = new Map();
  const find = (x) => {
    if (!parent.has(x)) parent.set(x, x);
    let r = x;
    while (parent.get(r) !== r) r = parent.get(r);
    let cur = x;
    while (parent.get(cur) !== r) { const nxt = parent.get(cur); parent.set(cur, r); cur = nxt; }
    return r;
  };
  const union = (a, b) => { const ra = find(a); const rb = find(b); if (ra !== rb) parent.set(ra, rb); };
  members.forEach((m) => find(m.id));
  spouseEdges.forEach((e) => union(e.person1_id, e.person2_id));

  const memberMap = new Map(members.map((m) => [m.id, m]));

  // Group members into units. Cap size to 2 (defensive: extra spouses go
  // to their own unit via the earliest linked spouse).
  const unitById = new Map();
  members.forEach((m) => {
    const uid = find(m.id);
    if (!unitById.has(uid)) unitById.set(uid, { id: uid, members: [], children: [], parentUnitId: null, x: 0, y: 0, w: TREE_CARD_W });
    unitById.get(uid).members.push(m.id);
  });

  // Sort within-unit: male-first, then earliest birth year.
  for (const u of unitById.values()) {
    u.members.sort((a, b) => {
      const ma = memberMap.get(a); const mb = memberMap.get(b);
      const ga = (ma?.gender || "").toLowerCase();
      const gb = (mb?.gender || "").toLowerCase();
      if (ga !== gb) { if (ga === "male") return -1; if (gb === "male") return 1; }
      return (ma?.birth_date || "").localeCompare(mb?.birth_date || "");
    });
    if (u.members.length > 2) u.members = u.members.slice(0, 2);
    u.w = u.members.length >= 2 ? TREE_CARD_W * 2 + COUPLE_GAP : TREE_CARD_W;
  }

  const memberToUnit = new Map();
  for (const u of unitById.values()) for (const mid of u.members) memberToUnit.set(mid, u.id);

  // For each member with parents, pick the first parent's unit.
  const memberParentUnit = new Map();
  for (const e of parentEdges) {
    if (!memberParentUnit.has(e.person2_id)) {
      const pUnit = memberToUnit.get(e.person1_id);
      if (pUnit) memberParentUnit.set(e.person2_id, pUnit);
    }
  }

  // Choose ONE primary parent-unit per unit: the parent-unit of the
  // earliest-born (or first-listed) member of the unit that has parents.
  for (const u of unitById.values()) {
    let bestMid = null;
    let bestBirth = null;
    for (const mid of u.members) {
      if (memberParentUnit.has(mid)) {
        const bd = memberMap.get(mid)?.birth_date || "";
        if (bestMid == null || (bd && (!bestBirth || bd < bestBirth))) {
          bestMid = mid; bestBirth = bd || bestBirth;
        }
      }
    }
    if (bestMid != null) {
      const pu = memberParentUnit.get(bestMid);
      if (pu && pu !== u.id) u.parentUnitId = pu;
    }
  }

  // Attach children arrays and break any accidental cycles.
  const isDescendant = (ancestorId, targetId) => {
    // Does targetId appear in the descendants of ancestorId?
    if (ancestorId === targetId) return true;
    const stack = [ancestorId];
    const seen = new Set();
    while (stack.length) {
      const cur = stack.pop();
      if (seen.has(cur)) continue;
      seen.add(cur);
      const cu = unitById.get(cur);
      if (!cu) continue;
      for (const cid of cu.children) {
        if (cid === targetId) return true;
        stack.push(cid);
      }
    }
    return false;
  };
  for (const u of unitById.values()) {
    if (u.parentUnitId && unitById.has(u.parentUnitId)) {
      // Guard: if attaching would create a cycle, drop the link (u becomes a root).
      if (isDescendant(u.id, u.parentUnitId)) { u.parentUnitId = null; continue; }
      unitById.get(u.parentUnitId).children.push(u.id);
    }
  }

  // Sort siblings by earliest birth year of primary member.
  for (const u of unitById.values()) {
    u.children.sort((a, b) => {
      const ua = unitById.get(a); const ub = unitById.get(b);
      const ba = memberMap.get(ua.members[0])?.birth_date || "";
      const bb = memberMap.get(ub.members[0])?.birth_date || "";
      return ba.localeCompare(bb);
    });
  }

  const roots = [];
  for (const u of unitById.values()) if (!u.parentUnitId) roots.push(u.id);

  // Total descendants per unit (ignoring collapse — used for the "+N hidden"
  // chip on collapse toggles). Memoized via post-order traversal.
  const totalDescCache = new Map();
  const totalDesc = (uid, seen = new Set()) => {
    if (totalDescCache.has(uid)) return totalDescCache.get(uid);
    if (seen.has(uid)) return 0;
    seen.add(uid);
    const u = unitById.get(uid);
    if (!u || !u.children.length) { totalDescCache.set(uid, 0); return 0; }
    let n = 0;
    for (const c of u.children) n += 1 + totalDesc(c, seen);
    totalDescCache.set(uid, n);
    return n;
  };
  for (const u of unitById.values()) u.descendantCount = totalDesc(u.id);

  // Sort roots: those with the most descendants first (main branch on left).
  roots.sort((a, b) => (unitById.get(b).descendantCount) - (unitById.get(a).descendantCount));

  // Compute subtree widths (respecting collapsed subtrees).
  const swCache = new Map();
  const subtreeW = (uid) => {
    if (swCache.has(uid)) return swCache.get(uid);
    const u = unitById.get(uid);
    if (!u) return 0;
    if (isCollapsed(uid) || !u.children.length) { swCache.set(uid, u.w); return u.w; }
    let total = 0;
    for (let i = 0; i < u.children.length; i++) {
      total += subtreeW(u.children[i]);
      if (i > 0) total += SIBLING_GAP;
    }
    const w = Math.max(total, u.w);
    swCache.set(uid, w);
    return w;
  };

  // Top-down placement.
  const placed = new Set();
  const place = (uid, startX, depth) => {
    const u = unitById.get(uid);
    if (!u) return;
    placed.add(uid);
    u.y = depth * ROW_H;
    const sw = subtreeW(uid);
    if (isCollapsed(uid) || !u.children.length) {
      u.x = startX + (sw - u.w) / 2;
      return;
    }
    let cursor = startX;
    for (const cid of u.children) {
      const cw = subtreeW(cid);
      place(cid, cursor, depth + 1);
      cursor += cw + SIBLING_GAP;
    }
    const first = unitById.get(u.children[0]);
    const last = unitById.get(u.children[u.children.length - 1]);
    const cx = (first.x + first.w / 2 + last.x + last.w / 2) / 2;
    u.x = cx - u.w / 2;
    if (u.x < startX) u.x = startX + (sw - u.w) / 2;
  };

  let cursorX = 0;
  for (const rid of roots) {
    place(rid, cursorX, 0);
    cursorX += subtreeW(rid) + SIBLING_GAP * 2;
  }

  // Shift by padding — only units that were actually placed.
  let maxX = 0, maxY = 0;
  for (const u of unitById.values()) {
    if (!placed.has(u.id)) { u.x = null; u.y = null; continue; }
    u.x += H_PAD;
    u.y += V_PAD;
    maxX = Math.max(maxX, u.x + u.w);
    maxY = Math.max(maxY, u.y + TREE_CARD_H);
  }

  // Orphans: members whose unit is a root AND has no children AND no
  // spouse (unit size 1). Detach from the canvas — render as a chip strip.
  const orphans = [];
  for (const u of unitById.values()) {
    if (u.members.length === 1 && !u.parentUnitId && u.children.length === 0) {
      orphans.push(u.members[0]);
    }
  }
  const orphanUnitIds = new Set(
    Array.from(unitById.values())
      .filter((u) => u.members.length === 1 && !u.parentUnitId && u.children.length === 0)
      .map((u) => u.id)
  );

  return {
    unitById,
    roots,
    width: maxX + H_PAD,
    height: maxY + V_PAD,
    orphans,
    orphanUnitIds,
  };
}


function TreeView({ members, rels, tree, isAdmin, onChanged }) {
  const treeSlug = tree?.slug || tree?.id;
  const collapseKey = `ft-tree-collapsed-${treeSlug || "unknown"}`;

  // Persist collapsed unit IDs per tree in localStorage.
  const [collapsedIds, setCollapsedIds] = useState(() => {
    try {
      const raw = localStorage.getItem(collapseKey);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  // Quick-edit drawer: when non-null, renders the right-side edit panel
  // for that member without navigating away from the tree canvas.
  const [quickEditId, setQuickEditId] = useState(null);
  // When the visitor navigates to a different tree, reload the collapsed
  // set for that tree so state doesn't bleed across pages.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(collapseKey);
      setCollapsedIds(raw ? new Set(JSON.parse(raw)) : new Set());
    } catch { setCollapsedIds(new Set()); }
  }, [collapseKey]);
  useEffect(() => {
    try { localStorage.setItem(collapseKey, JSON.stringify([...collapsedIds])); } catch { /* quota */ }
  }, [collapseKey, collapsedIds]);

  const toggleCollapse = useCallback((uid) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  }, []);

  const layout = useMemo(() => buildFamilyLayout(members, rels, collapsedIds), [members, rels, collapsedIds]);
  const memberById = useMemo(() => {
    const m = new Map();
    members.forEach((x) => m.set(x.id, x));
    return m;
  }, [members]);

  // Collapse/Expand all — every unit that has children.
  const collapseAll = () => {
    const s = new Set();
    for (const u of layout.unitById.values()) {
      if (u.children.length > 0 && !layout.orphanUnitIds.has(u.id)) s.add(u.id);
    }
    setCollapsedIds(s);
  };
  const expandAll = () => setCollapsedIds(new Set());

  const [scale, setScale] = useState(1.0);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ active: false, sx: 0, sy: 0, tx0: 0, ty0: 0, moved: false });
  const canvasRef = useRef(null);
  // True once the visitor has manually panned, zoomed, or fit — we then
  // stop the auto-center effect from re-firing on layout re-computations
  // (which happen on browser-zoom, sidebar toggle, member add, etc.).
  const hasUserInteractedRef = useRef(false);
  const markUserInteracted = () => { hasUserInteractedRef.current = true; };

  const zoomIn  = () => { markUserInteracted(); setScale((s) => Math.min(+(s + 0.15).toFixed(2), 2.5)); };
  const zoomOut = () => { markUserInteracted(); setScale((s) => Math.max(+(s - 0.15).toFixed(2), 0.15)); };
  const zoomFit = useCallback(() => {
    markUserInteracted();
    const box = canvasRef.current?.getBoundingClientRect();
    if (!box || !layout.width || !layout.height) return;
    const sx = (box.width - 20) / layout.width;
    const sy = (box.height - 20) / layout.height;
    const s = Math.max(0.15, Math.min(1.2, Math.min(sx, sy)));
    setScale(s);
    setTx(Math.max(0, (box.width - layout.width * s) / 2));
    setTy(10);
  }, [layout.width, layout.height]);
  const zoomReset = () => { hasUserInteractedRef.current = false; centerOnFirstRoot(); markUserInteracted(); };

  // Center on the primary root couple at 100% zoom. Ensures the tree
  // opens on a legible, readable snapshot instead of a distant "fit-all"
  // that shows nothing but tiny dots. Falls back to Fit if there's no
  // room at 100%.
  const centerOnFirstRoot = useCallback(() => {
    const box = canvasRef.current?.getBoundingClientRect();
    const firstRootId = layout.roots.find((r) => !layout.orphanUnitIds.has(r));
    const firstRoot = firstRootId ? layout.unitById.get(firstRootId) : null;
    if (!box || !firstRoot) return;
    const s = 1.0;
    setScale(s);
    // Place the root couple horizontally centred, near the top.
    const cx = firstRoot.x + firstRoot.w / 2;
    setTx(box.width / 2 - cx * s);
    setTy(24);
  }, [layout.roots, layout.unitById, layout.orphanUnitIds]);

  // Auto-center ONCE on first render — ref guard prevents the effect
  // from stomping on the user's pan/zoom every time the layout changes
  // (browser-zoom, viewport resize, sidebar toggle, add-member, etc.).
  const didAutoCenterRef = useRef(false);
  useEffect(() => {
    if (didAutoCenterRef.current) return;
    if (hasUserInteractedRef.current) return;
    const t = setTimeout(() => {
      centerOnFirstRoot();
      didAutoCenterRef.current = true;
    }, 50);
    return () => clearTimeout(t);
  }, [centerOnFirstRoot]);

  const onMouseDown = (e) => {
    // Ignore drags that start on a card — let the link handle click.
    if (e.target.closest("a[data-testid^='tree-node-']")) return;
    dragRef.current = { active: true, sx: e.clientX, sy: e.clientY, tx0: tx, ty0: ty, moved: false };
    setDragging(true);
    markUserInteracted();
  };
  const onMouseMove = (e) => {
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.sx;
    const dy = e.clientY - dragRef.current.sy;
    if (Math.abs(dx) + Math.abs(dy) > 3) dragRef.current.moved = true;
    setTx(dragRef.current.tx0 + dx);
    setTy(dragRef.current.ty0 + dy);
  };
  const onMouseUp = () => { dragRef.current.active = false; setDragging(false); };

  const onWheel = (e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    markUserInteracted();
    const box = canvasRef.current?.getBoundingClientRect();
    const cx = box ? e.clientX - box.left : 0;
    const cy = box ? e.clientY - box.top : 0;
    setScale((s) => {
      const next = Math.max(0.15, Math.min(2.5, s * (e.deltaY < 0 ? 1.12 : 1 / 1.12)));
      // Zoom around cursor.
      const k = next / s;
      setTx((t) => cx - k * (cx - t));
      setTy((t) => cy - k * (cy - t));
      return next;
    });
  };

  if (members.length === 0) {
    return (
      <div className="wofy-card text-center py-16">
        <Users className="w-6 h-6 mx-auto mb-2" style={{ color: "var(--wofy-primary)" }} />
        <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>No members yet. Add your first family member to start the tree.</p>
      </div>
    );
  }

  // Build SVG connector paths.
  const connectors = [];
  for (const u of layout.unitById.values()) {
    if (!u.children.length) continue;
    if (layout.orphanUnitIds.has(u.id)) continue;
    if (collapsedIds.has(u.id)) continue;               // hide connectors for collapsed subtrees
    if (u.x == null || u.y == null) continue;           // skip unplaced units
    const parentBottomX = u.x + u.w / 2;
    const parentBottomY = u.y + TREE_CARD_H;
    const busY = parentBottomY + GEN_GAP / 2;
    const childCenters = u.children
      .map((cid) => layout.unitById.get(cid))
      .filter((c) => c && c.x != null && c.y != null)
      .map((c) => ({ x: c.x + c.w / 2, y: c.y }));
    if (!childCenters.length) continue;
    const minCX = Math.min(parentBottomX, ...childCenters.map((c) => c.x));
    const maxCX = Math.max(parentBottomX, ...childCenters.map((c) => c.x));
    // Parent down-stub
    connectors.push(
      <line key={`p-${u.id}`} x1={parentBottomX} y1={parentBottomY} x2={parentBottomX} y2={busY} />
    );
    // Horizontal bus if needed
    if (childCenters.length > 1 || Math.abs(childCenters[0].x - parentBottomX) > 0.5) {
      connectors.push(
        <line key={`b-${u.id}`} x1={minCX} y1={busY} x2={maxCX} y2={busY} />
      );
    }
    // Child down-stubs
    for (let i = 0; i < childCenters.length; i++) {
      const c = childCenters[i];
      connectors.push(
        <line key={`c-${u.id}-${i}`} x1={c.x} y1={busY} x2={c.x} y2={c.y} />
      );
    }
  }

  return (
    <div data-testid="family-tree-view">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
          {layout.unitById.size - layout.orphanUnitIds.size} unit{layout.unitById.size - layout.orphanUnitIds.size === 1 ? "" : "s"} · {members.length} people · {layout.roots.filter((r) => !layout.orphanUnitIds.has(r)).length} root branch{layout.roots.filter((r) => !layout.orphanUnitIds.has(r)).length === 1 ? "" : "es"}{collapsedIds.size > 0 ? ` · ${collapsedIds.size} collapsed` : ""}
        </div>
        <div className="inline-flex items-center gap-1 flex-wrap">
          <button type="button" onClick={expandAll}   className="wofy-btn-ghost text-xs px-2" data-testid="tree-expand-all"   aria-label="Expand all branches" disabled={collapsedIds.size === 0}>Expand all</button>
          <button type="button" onClick={collapseAll} className="wofy-btn-ghost text-xs px-2" data-testid="tree-collapse-all" aria-label="Collapse all branches">Collapse all</button>
          <span className="w-px h-4 mx-1" style={{ background: "var(--wofy-line)" }} aria-hidden />
          <button type="button" onClick={zoomOut} className="wofy-btn-ghost text-xs px-2" data-testid="tree-zoom-out" aria-label="Zoom out">−</button>
          <span className="text-[10px] font-mono tabular-nums px-1 min-w-[3.5rem] text-center" data-testid="tree-zoom-value">{Math.round(scale * 100)}%</span>
          <button type="button" onClick={zoomIn} className="wofy-btn-ghost text-xs px-2" data-testid="tree-zoom-in" aria-label="Zoom in">+</button>
          <button type="button" onClick={zoomFit} className="wofy-btn-ghost text-xs px-2" data-testid="tree-zoom-fit" aria-label="Fit to screen">Fit</button>
          <button type="button" onClick={zoomReset} className="wofy-btn-ghost text-xs px-2" data-testid="tree-zoom-reset" aria-label="Reset zoom">Reset</button>
        </div>
      </div>

      <div
        ref={canvasRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onWheel={onWheel}
        className="relative border rounded-lg bg-white overflow-hidden select-none"
        style={{
          borderColor: "var(--wofy-border)",
          height: "78vh",
          cursor: dragging ? "grabbing" : "grab",
          touchAction: "none",
          backgroundImage:
            "radial-gradient(circle, rgba(139,92,246,0.06) 1px, transparent 1px)",
          backgroundSize: "22px 22px",
        }}
        data-testid="tree-canvas"
      >
        <div
          className="absolute top-0 left-0 origin-top-left"
          style={{
            transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`,
            width: layout.width,
            height: layout.height,
          }}
          data-testid="tree-inner"
        >
          <svg
            width={layout.width}
            height={layout.height}
            style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
            aria-hidden
          >
            <g stroke="var(--wofy-primary)" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.85">
              {connectors}
            </g>
          </svg>

          {Array.from(layout.unitById.values()).map((u) => {
            if (layout.orphanUnitIds.has(u.id)) return null;
            if (u.x == null || u.y == null) return null;   // hidden inside a collapsed subtree
            const a = memberById.get(u.members[0]);
            const b = u.members[1] ? memberById.get(u.members[1]) : null;
            const hasKids = u.children.length > 0;
            const isCollapsed = collapsedIds.has(u.id);
            return (
              <div
                key={u.id}
                style={{
                  position: "absolute",
                  left: u.x,
                  top: u.y,
                  width: u.w,
                  height: TREE_CARD_H,
                  display: "flex",
                  alignItems: "stretch",
                }}
                data-testid={`family-unit-${u.id}`}
              >
                {a && <TreeMiniCard m={a} treeSlug={treeSlug} deceased={!!a.death_date} isAdmin={isAdmin} onQuickEdit={() => setQuickEditId(a.id)} />}
                {b && (
                  <>
                    <div style={{ alignSelf: "center", width: COUPLE_GAP, height: 2, background: "var(--wofy-primary)" }} aria-hidden />
                    <TreeMiniCard m={b} treeSlug={treeSlug} deceased={!!b.death_date} isAdmin={isAdmin} onQuickEdit={() => setQuickEditId(b.id)} />
                  </>
                )}
                {hasKids && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleCollapse(u.id); }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="absolute rounded-full shadow-sm border bg-white hover:bg-purple-50 transition-colors inline-flex items-center justify-center gap-0.5 px-1.5"
                    style={{
                      left: u.w / 2 - (isCollapsed ? 22 : 11),
                      bottom: -12,
                      height: 22,
                      minWidth: 22,
                      borderColor: "var(--wofy-primary)",
                      color: "var(--wofy-primary)",
                      fontSize: 10,
                      lineHeight: 1,
                      zIndex: 2,
                    }}
                    data-testid={`family-unit-toggle-${u.id}`}
                    aria-label={isCollapsed ? `Expand ${u.descendantCount} descendants` : "Collapse this branch"}
                    title={isCollapsed ? `Show ${u.descendantCount} descendant${u.descendantCount === 1 ? "" : "s"}` : "Hide this branch"}
                  >
                    {isCollapsed ? (
                      <>
                        <ChevronDown className="w-3 h-3" strokeWidth={2.5} />
                        <span className="font-mono font-semibold">+{u.descendantCount}</span>
                      </>
                    ) : (
                      <ChevronUp className="w-3 h-3" strokeWidth={2.5} />
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {layout.orphans.length > 0 && (
        <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--wofy-line)" }} data-testid="tree-orphans">
          <div className="text-[10px] font-mono uppercase tracking-wider mb-2" style={{ color: "var(--wofy-text-soft)" }}>
            Not yet linked · {layout.orphans.length}
            {isAdmin && <span className="ml-2 normal-case opacity-60">— click the trash icon to remove, or the pencil to quick-edit</span>}
          </div>
          <div className="flex gap-2 flex-wrap">
            {layout.orphans.map((id) => {
              const m = memberById.get(id);
              if (!m) return null;
              return (
                <OrphanCard
                  key={id}
                  m={m}
                  treeSlug={treeSlug}
                  treeId={tree?.id}
                  isAdmin={!!isAdmin}
                  onChanged={onChanged}
                  onQuickEdit={() => setQuickEditId(id)}
                />
              );
            })}
          </div>
        </div>
      )}

      <p className="text-[10px] mt-2 opacity-60">
        Drag the canvas to pan · Ctrl/⌘ + scroll or use +/− to zoom · Click the chevron under any couple to collapse or expand that branch · Tap a card to open the profile.
      </p>

      {quickEditId && (
        <QuickEditDrawer
          memberId={quickEditId}
          tree={tree}
          onClose={() => setQuickEditId(null)}
          onSaved={() => { if (typeof onChanged === "function") onChanged(); }}
        />
      )}
    </div>
  );
}

// Legacy card kept for future use — no longer rendered in the tree canvas.
// eslint-disable-next-line no-unused-vars
function MemberCard({ m, treeId, treeSlug, user, tree }) {
  const name = [m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ");
  const dateStr = (d, yearOnly) => {
    if (!d) return "";
    if (yearOnly) return d.slice(0, 4);
    if (d.length === 4) return d;
    return d.slice(0, 4);
  };
  const b = dateStr(m.birth_date, m.birth_date_is_year_only);
  const dth = dateStr(m.death_date, m.death_date_is_year_only);
  const dates = [b, dth].filter(Boolean).join(" – ");
  const deceased = !!m.death_date;
  return (
    <div
      data-testid={`family-member-${m.id}`}
      className="w-44 rounded-xl border p-2 text-center relative"
      style={{
        borderColor: "var(--wofy-border)",
        background: deceased ? "rgba(139,92,246,0.05)" : "var(--wofy-paper)",
      }}
    >
      <Link
        to={`/family/${treeSlug || treeId}/member/${m.id}`}
        className="block group"
        title="Open full profile"
        data-testid={`family-member-open-${m.id}`}
      >
        <div className="w-24 h-24 mx-auto rounded-full overflow-hidden mb-1.5 border-2 group-hover:scale-105 transition-transform relative" style={{ borderColor: deceased ? "#5B4A73" : "var(--wofy-primary)" }}>
          {m.photo ? (
            <img src={m.photo} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-2xl" style={{ background: "var(--wofy-surface)" }}>
              {name?.[0] || "?"}
            </div>
          )}
          <CopyLinkIcon
            url={m.memorial_slug ? `/celebrate/${m.memorial_slug}` : `/family/${treeSlug || treeId}/member/${m.id}`}
            position="tr"
            label={m.memorial_slug ? "Copy memorial link" : "Copy profile link"}
            testId={`family-member-copy-${m.id}`}
          />
        </div>
        <div className="font-semibold text-sm leading-snug group-hover:underline">{name}</div>
        {m.nickname && (
          <div className="text-[11px] italic leading-tight" style={{ color: "var(--wofy-primary)" }} data-testid={`family-member-nickname-${m.id}`} title="Nickname">
            "{m.nickname}"
          </div>
        )}
        {dates && <div className="text-[10px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>{dates}</div>}
        {m.location && !deceased && (
          <div className="text-[10px] leading-tight mt-0.5 inline-flex items-center gap-0.5 justify-center max-w-full" style={{ color: "var(--wofy-text-soft)" }} data-testid={`family-member-location-${m.id}`} title="Current place">
            <MapPin className="w-2.5 h-2.5 flex-none opacity-60" />
            <span className="truncate">{m.location}</span>
          </div>
        )}
      </Link>

      {/* Memorial CTA — deceased members */}
      {deceased && (
        <MemorialCta member={m} />
      )}

      {/* Claim for Edit — every registered user can request */}
      {user && (
        <ClaimForEditButton treeId={treeId} member={m} />
      )}

      {/* Contact this member (Admin/Member badge) — every logged-in user */}
      {user && (
        <ContactMemberButton treeId={treeId} member={m} tree={tree} />
      )}
    </div>
  );
}

function ContactMemberButton({ treeId, member, tree }) {
  const [open, setOpen] = useState(false);
  const uid = member.claimed_by_user_id;
  const isTreeAdmin = tree && (uid === (tree.owner_id) || (tree.admin_ids || []).includes(uid));
  const role = isTreeAdmin ? "admin" : "member";
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="mt-1.5 text-[10px] inline-flex items-center gap-1 rounded-full px-2 py-0.5 border w-full justify-center"
        style={{ borderColor: "var(--wofy-border)", color: "#2563eb" }}
        data-testid={`family-member-contact-${member.id}`}
      >
        <MessageCircle className="w-3 h-3" />
        {role === "admin" ? "Admin · Contact" : "Member · Contact"}
      </button>
      {open && (
        <FamilyMemberContactModal
          treeId={treeId}
          member={member}
          role={role}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function MemorialCta({ member }) {
  if (member.memorial_slug) {
    return (
      <Link
        to={`/celebrate/${member.memorial_slug}`}
        className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold underline"
        style={{ color: "#5B4A73" }}
        data-testid={`family-member-memorial-${member.id}`}
      >
        <Flower2 className="w-3 h-3" /> Visit Memorial
      </Link>
    );
  }
  const name = [member.first_name, member.middle_name, member.last_name].filter(Boolean).join(" ");
  const qs = new URLSearchParams({
    from_family_member: member.id || "",
    name: name,
    birth: member.birth_date || "",
    death: member.death_date || "",
    birth_place: member.birth_place || "",
    death_place: member.death_place || "",
  }).toString();
  return (
    <Link
      to={`/celebrate/submit?${qs}`}
      className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5"
      style={{ background: "#5B4A73", color: "white" }}
      data-testid={`family-member-create-memorial-${member.id}`}
    >
      <Flower2 className="w-3 h-3" /> Create Memorial
    </Link>
  );
}

function ClaimForEditButton({ treeId, member }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [rel, setRel] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | pending | already
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post(`/family-trees/${treeId}/members/${member.id}/claim`, {
        note: note.trim() || undefined,
        relationship: rel.trim() || undefined,
      });
      const s = data.status;
      if (s === "already_editor") { toast.success("You already have edit access."); setStatus("already"); }
      else if (s === "already_pending") { toast.info("Your claim is already pending."); setStatus("pending"); }
      else { toast.success("Claim sent. The owner will review it."); setStatus("pending"); }
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send claim");
    } finally { setBusy(false); }
  };
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        disabled={status === "pending" || status === "already"}
        className="mt-1.5 text-[10px] inline-flex items-center gap-1 rounded-full px-2 py-0.5 border w-full justify-center disabled:opacity-60"
        style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-primary)" }}
        data-testid={`family-member-claim-${member.id}`}
      >
        <UserPlus className="w-3 h-3" />
        {status === "pending" ? "Claim pending" : status === "already" ? "Editor ✓" : "Claim for Edit"}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-[#14213D]/25 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} className="wofy-card w-full max-w-md text-left" onClick={(e) => e.stopPropagation()} data-testid={`claim-modal-${member.id}`}>
            <div className="overline mb-2">CLAIM FOR EDIT</div>
            <h3 className="font-display text-xl mb-1">
              Request to edit <span className="italic">{[member.first_name, member.last_name].filter(Boolean).join(" ")}</span>
            </h3>
            <p className="text-xs mb-3" style={{ color: "var(--wofy-text-soft)" }}>
              The tree owner will review your request. If approved, you'll be able to add photos, correct details, and enrich this member's profile.
            </p>
            <input
              className="wofy-input mb-2"
              placeholder="Your relationship (e.g. grandson, cousin, close friend)"
              value={rel}
              onChange={(e) => setRel(e.target.value)}
              maxLength={120}
              data-testid={`claim-rel-${member.id}`}
            />
            <textarea
              className="wofy-input"
              placeholder="Optional note — why you'd like to edit, what you can contribute…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={500}
              data-testid={`claim-note-${member.id}`}
            />
            <div className="flex justify-end gap-2 mt-3">
              <button type="button" onClick={() => setOpen(false)} className="wofy-btn-ghost text-sm">Cancel</button>
              <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid={`claim-submit-${member.id}`}>
                {busy ? "Sending…" : "Send claim"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Members list (table).                                              */
/* ------------------------------------------------------------------ */
function MembersList({ members, isAdmin, treeId, treeSlug, tree, onDeleted, user }) {
  const [openComments, setOpenComments] = useState(null);
  const [contactMember, setContactMember] = useState(null); // opens the visitor contact form
  const del = async (id) => {
    if (!window.confirm("Remove this member? Their relationships will also be removed.")) return;
    try {
      await http.delete(`/family-trees/${treeId}/members/${id}`);
      toast.success("Member removed");
      onDeleted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const fmtDate = (d, yearOnly) => {
    if (!d) return "";
    if (yearOnly || d.length === 4) return d.slice(0, 4);
    return d;
  };

  // Map member.user_id → tree role so cards can badge Admin/Editor.
  const memberRole = useCallback((m) => {
    if (!m?.user_id || !tree) return null;
    if (tree.owner_id === m.user_id) return "Admin";
    if ((tree.admin_user_ids || []).includes(m.user_id)) return "Admin";
    if ((tree.editor_user_ids || []).includes(m.user_id)) return "Editor";
    return null;
  }, [tree]);

  // Public trees or private trees you have access to → card is a link
  // to the full profile. Private without access → link is disabled.
  const isPublic = (tree?.visibility || "public") === "public";
  const canOpenProfile = isPublic || isAdmin || (user && (tree?.member_user_ids || []).includes(user.id));

  if (members.length === 0) return <div className="wofy-card text-sm text-center" style={{ color: "var(--wofy-text-soft)" }}>No members yet.</div>;
  return (
    <div>
      <TreeAdminsStrip tree={tree} treeSlug={treeSlug} />
      <div className="grid md:grid-cols-2 gap-3" data-testid="ft-members-grid">
      {members.map((m) => {
        const name = [m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ");
        const b = fmtDate(m.birth_date, m.birth_date_is_year_only);
        const dth = fmtDate(m.death_date, m.death_date_is_year_only);
        const dateLine = [b, dth].filter(Boolean).join(" – ") || "Dates unknown";
        const role = memberRole(m);
        const profileHref = canOpenProfile ? `/family/${treeSlug}/member/${m.id}` : null;

        const cardInner = (
          <>
            <div className="w-14 h-14 rounded-full overflow-hidden shrink-0 border" style={{ borderColor: "var(--wofy-border)" }}>
              {m.photo ? <img src={m.photo} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center bg-[var(--wofy-surface)]">{name?.[0] || "?"}</div>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold">{name}</div>
                {role && (
                  <span
                    className="px-1.5 py-0.5 rounded text-[10px] font-mono uppercase"
                    style={{ background: role === "Admin" ? "#DBEAFE" : "#FEF3C7", color: role === "Admin" ? "#1E40AF" : "#92400E" }}
                    data-testid={`ft-member-role-${m.id}`}
                  >
                    {role}
                  </span>
                )}
              </div>
              {m.other_names && (
                <div className="text-xs italic" style={{ color: "var(--wofy-text-soft)" }}>
                  Also known as: {pickI18n(m.other_names)}
                </div>
              )}
              <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
                {dateLine}{m.birth_place && ` · ${m.birth_place}`}
              </div>
              {m.bio && <p className="text-xs mt-1 line-clamp-2">{pickI18n(m.bio)}</p>}
              <div className="flex items-center gap-3 mt-2" onClick={(e) => e.stopPropagation()}>
                {m.memorial_slug && (
                  <Link to={`/celebrate/${m.memorial_slug}`} className="text-xs underline inline-flex items-center gap-0.5" style={{ color: "#5B4A73" }}>
                    <Flower2 className="w-3 h-3" /> Memorial
                  </Link>
                )}
                {user && (
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpenComments(openComments === m.id ? null : m.id); }}
                    data-testid={`ft-comments-toggle-${m.id}`}
                    className="text-xs inline-flex items-center gap-0.5 hover:underline"
                    style={{ color: "var(--wofy-primary)" }}
                  >
                    <MessageCircle className="w-3 h-3" /> {openComments === m.id ? "Hide" : "Comments"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setContactMember(m); }}
                  className="text-xs inline-flex items-center gap-0.5 hover:underline font-medium ml-auto"
                  style={{ color: "var(--wofy-primary)" }}
                  data-testid={`ft-member-contact-${m.id}`}
                >
                  Contact &raquo;
                </button>
              </div>
              {openComments === m.id && (
                <MemberComments treeId={treeId} memberId={m.id} user={user} />
              )}
            </div>
            {isAdmin && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); del(m.id); }}
                className="opacity-50 hover:opacity-100"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" style={{ color: "#B23A3A" }} />
              </button>
            )}
          </>
        );

        return profileHref ? (
          <Link
            key={m.id}
            to={profileHref}
            className="wofy-card flex items-start gap-3 hover:shadow-md transition"
            data-testid={`ft-member-card-${m.id}`}
          >
            {cardInner}
          </Link>
        ) : (
          <div
            key={m.id}
            className="wofy-card flex items-start gap-3 cursor-not-allowed"
            title="This tree is private — request access to see the full profile"
            data-testid={`ft-member-card-${m.id}`}
          >
            {cardInner}
          </div>
        );
      })}
      {contactMember && (
        <MemberContactModal
          treeSlug={treeSlug}
          member={contactMember}
          onClose={() => setContactMember(null)}
        />
      )}
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Admins strip — persistent visible list of tree Admins & Editors    */
/* with a click-to-contact button so visitors always know who to      */
/* reach for access requests, corrections, or new information.        */
/* ------------------------------------------------------------------ */
function TreeAdminsStrip({ tree, treeSlug }) {
  const [state, setState] = useState({ loading: true, items: [] });
  const [openModal, setOpenModal] = useState(null); // {kind:'admin', user:{name,handle,id}}

  useEffect(() => {
    if (!tree?.id) return;
    http.get(`/family-trees/${tree.id}/roles`)
      .then((r) => {
        const d = r.data || {};
        // Endpoint returns {owner, admins:[], editors:[]}. Treat the
        // owner as an Admin so the strip is never empty for trees that
        // only have a solo owner.
        const owner = d.owner ? [{ ...d.owner, role: "admin" }] : [];
        const admins = [...owner, ...(d.admins || [])];
        const editors = d.editors || [];
        setState({ loading: false, items: [...admins, ...editors] });
      })
      .catch(() => setState({ loading: false, items: [] }));
  }, [tree?.id]);

  const admins  = (state.items || []).filter((r) => (r.role || "").toLowerCase() === "admin" || (r.role || "").toLowerCase() === "owner");
  const editors = (state.items || []).filter((r) => (r.role || "").toLowerCase() === "editor");

  if (state.loading || (admins.length === 0 && editors.length === 0)) return null;

  return (
    <div
      className="mb-4 rounded-xl border p-3"
      style={{ borderColor: "var(--wofy-border)", background: "linear-gradient(135deg,#EFF6FF 0%,#FEF3C7 100%)" }}
      data-testid="ft-admins-strip"
    >
      <div className="text-xs font-mono uppercase mb-2 opacity-70">
        Tree admins &amp; editors — contact them to add yourself or request access
      </div>
      <div className="flex flex-wrap gap-2">
        {admins.map((r) => (
          <button
            key={`admin-${r.id || r.user_id}`}
            type="button"
            onClick={() => setOpenModal({ user_name: r.name || r.email || r.handle, role: "Admin" })}
            className="inline-flex items-center gap-2 rounded-full border pl-1 pr-3 py-1 bg-white hover:shadow-sm"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid={`ft-admin-strip-${r.id || r.user_id}`}
          >
            {(r.avatar_url || r.profile_picture || r.photo)
              ? <img src={resolveImage(r.avatar_url || r.profile_picture || r.photo)} alt="" className="w-6 h-6 rounded-full object-cover" />
              : <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-800 text-xs flex items-center justify-center">
                  {(r.name || r.email || r.handle || "?")[0]}
                </span>}
            <span className="text-xs font-medium">{r.name || r.email || r.handle || "Admin"}</span>
            <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded" style={{ background: "#DBEAFE", color: "#1E40AF" }}>
              Admin
            </span>
          </button>
        ))}
        {editors.map((r) => (
          <button
            key={`editor-${r.id || r.user_id}`}
            type="button"
            onClick={() => setOpenModal({ user_name: r.name || r.email || r.handle, role: "Editor" })}
            className="inline-flex items-center gap-2 rounded-full border pl-1 pr-3 py-1 bg-white hover:shadow-sm"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid={`ft-editor-strip-${r.id || r.user_id}`}
          >
            {(r.avatar_url || r.profile_picture || r.photo)
              ? <img src={resolveImage(r.avatar_url || r.profile_picture || r.photo)} alt="" className="w-6 h-6 rounded-full object-cover" />
              : <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-800 text-xs flex items-center justify-center">
                  {(r.name || r.email || r.handle || "?")[0]}
                </span>}
            <span className="text-xs font-medium">{r.name || r.email || r.handle || "Editor"}</span>
            <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded" style={{ background: "#FEF3C7", color: "#92400E" }}>
              Editor
            </span>
          </button>
        ))}
      </div>
      {openModal && (
        <TreeContactModal
          treeSlug={treeSlug}
          treeName={tree?.name}
          adminName={openModal.user_name}
          role={openModal.role}
          onClose={() => setOpenModal(null)}
        />
      )}
    </div>
  );
}

/* Lightweight modal that posts to the tree's owner via the shared
 * `member_contact_requests` endpoint (using the tree owner as the
 * "member" so admins receive an email + inbox notification).        */
function TreeContactModal({ treeSlug, treeName, adminName, role, onClose }) {
  const [yourName, setYourName] = useState("");
  const [yourEmail, setYourEmail] = useState("");
  const [subject, setSubject] = useState("add_me");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!yourName.trim() || !yourEmail.trim() || !message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeSlug}/tree-contact`, {
        your_name: yourName.trim(),
        your_email: yourEmail.trim(),
        subject,
        message: message.trim(),
      });
      setSent(true);
      toast.success("Message delivered to the tree admins.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the message");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="ft-tree-contact-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="overline text-xs">CONTACT REQUEST</div>
            <h3 className="font-display text-xl mt-1">
              Reach {adminName} ({role}) about {treeName}
            </h3>
            <p className="text-xs opacity-70 mt-1">
              Your message goes to all tree admins. They'll reply directly to your email.
            </p>
          </div>
          <button onClick={onClose} className="opacity-50 hover:opacity-100 text-2xl leading-none">×</button>
        </div>
        {sent ? (
          <div className="mt-6 rounded-lg border p-4 text-sm" style={{ borderColor: "var(--wofy-border)", background: "#F0FDF4", color: "#166534" }} data-testid="ft-tree-contact-success">
            Thanks! Your message has been delivered. Admins will reply to <b>{yourEmail}</b>.
            <div className="mt-3">
              <button onClick={onClose} className="wofy-btn-ghost text-sm">Close</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input value={yourName} onChange={(e) => setYourName(e.target.value)} placeholder="Your name" className="rounded-lg border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--wofy-border)" }} data-testid="ft-tree-contact-name" required />
              <input type="email" value={yourEmail} onChange={(e) => setYourEmail(e.target.value)} placeholder="Your email" className="rounded-lg border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--wofy-border)" }} data-testid="ft-tree-contact-email" required />
            </div>
            <select value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-lg border px-3 py-2 text-sm w-full outline-none bg-white" style={{ borderColor: "var(--wofy-border)" }} data-testid="ft-tree-contact-subject">
              <option value="add_me">Please add me to this family tree</option>
              <option value="need_access">Requesting access</option>
              <option value="share_info">I have information / photos to share</option>
              <option value="correction">Correction or update needed</option>
              <option value="other">General enquiry</option>
            </select>
            <textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Add details — how you're related, what you'd like to share, or the update needed." className="rounded-lg border px-3 py-2 text-sm w-full outline-none" style={{ borderColor: "var(--wofy-border)" }} data-testid="ft-tree-contact-message" required />
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={onClose} className="wofy-btn-ghost text-sm">Cancel</button>
              <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid="ft-tree-contact-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {busy ? "Sending…" : "Send message"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Visitor contact modal — reaches the tree admin(s) by email + inbox */
/* ------------------------------------------------------------------ */
function MemberContactModal({ treeSlug, member, onClose }) {
  const memberName = [member.first_name, member.middle_name, member.last_name].filter(Boolean).join(" ") || "this member";
  const [yourName, setYourName] = useState("");
  const [yourEmail, setYourEmail] = useState("");
  const [subject, setSubject] = useState("add_me");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!yourName.trim() || !yourEmail.trim() || !message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeSlug}/members/${member.id}/contact`, {
        your_name: yourName.trim(),
        your_email: yourEmail.trim(),
        subject,
        message: message.trim(),
      });
      setSent(true);
      toast.success("Message sent to the tree admins.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the message");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
      data-testid="ft-contact-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="overline text-xs">CONTACT REQUEST</div>
            <h3 className="font-display text-xl mt-1">Reach the admins about {memberName}</h3>
            <p className="text-xs opacity-70 mt-1">
              Your message goes to the tree admins — not the member directly.
              They'll reply to your email.
            </p>
          </div>
          <button onClick={onClose} className="opacity-50 hover:opacity-100 text-2xl leading-none" data-testid="ft-contact-close">×</button>
        </div>

        {sent ? (
          <div className="mt-6 rounded-lg border p-4 text-sm" style={{ borderColor: "var(--wofy-border)", background: "#F0FDF4", color: "#166534" }} data-testid="ft-contact-success">
            Thanks! Your message has been delivered. The tree admins will get back to you at{" "}
            <b>{yourEmail}</b>.
            <div className="mt-3">
              <button onClick={onClose} className="wofy-btn-ghost text-sm">Close</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input
                value={yourName}
                onChange={(e) => setYourName(e.target.value)}
                placeholder="Your name"
                className="rounded-lg border px-3 py-2 text-sm outline-none"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid="ft-contact-name"
                required
              />
              <input
                type="email"
                value={yourEmail}
                onChange={(e) => setYourEmail(e.target.value)}
                placeholder="Your email"
                className="rounded-lg border px-3 py-2 text-sm outline-none"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid="ft-contact-email"
                required
              />
            </div>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="rounded-lg border px-3 py-2 text-sm w-full outline-none bg-white"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="ft-contact-subject"
            >
              <option value="add_me">Please add me to this family tree</option>
              <option value="need_access">Requesting access to view the full profile</option>
              <option value="share_info">I have information / photos to share</option>
              <option value="correction">Correction or update needed</option>
              <option value="other">General enquiry</option>
            </select>
            <textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add details — how you're related, what you'd like to share, or the update needed."
              className="rounded-lg border px-3 py-2 text-sm w-full outline-none"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="ft-contact-message"
              required
            />
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={onClose} className="wofy-btn-ghost text-sm">Cancel</button>
              <button
                type="submit"
                disabled={busy}
                className="wofy-btn-primary text-sm"
                data-testid="ft-contact-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {busy ? "Sending…" : "Send message"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Comments per member                                                */
/* ------------------------------------------------------------------ */
function MemberComments({ treeId, memberId, user }) {
  const [items, setItems] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const { data } = await http.get(`/family-trees/${treeId}/members/${memberId}/comments`);
      setItems(data.items);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [memberId]);
  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeId}/members/${memberId}/comments`, { body: body.trim() });
      setBody("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to post"); }
    finally { setBusy(false); }
  };
  const del = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try { await http.delete(`/family-trees/${treeId}/comments/${cid}`); load(); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <div className="mt-3 pt-3 border-t space-y-2" style={{ borderColor: "var(--wofy-line)" }} data-testid={`ft-comments-${memberId}`}>
      {items.length === 0 && <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>No comments yet — be the first to share a memory.</div>}
      {items.map((c) => (
        <div key={c.id} className="text-xs">
          <div className="flex items-baseline justify-between gap-2">
            <div className="font-semibold">{c.author_name}</div>
            {c.author_id === user?.id && (
              <button onClick={() => del(c.id)} className="opacity-40 hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
            )}
          </div>
          <p style={{ color: "var(--wofy-text-soft)" }}>{c.body}</p>
        </div>
      ))}
      {user && (
        <form onSubmit={submit} className="flex gap-1 pt-1">
          <input
            data-testid={`ft-comment-input-${memberId}`}
            className="wofy-input flex-1 text-xs"
            placeholder="Share a memory or note…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <button data-testid={`ft-comment-submit-${memberId}`} type="submit" disabled={busy} className="wofy-btn-primary !py-1 !px-3"><Send className="w-3 h-3" /></button>
        </form>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Events tab.                                                        */
/* ------------------------------------------------------------------ */
function EventsList({ events, treeId, onChanged, isAdmin }) {
  const [activeCat, setActiveCat] = useState("all");
  const rsvp = async (id, response) => {
    try {
      await http.post(`/family-trees/${treeId}/events/${id}/rsvp`, { response });
      toast.success(`RSVP: ${response}`);
      onChanged?.();
    } catch (e) { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try { await http.delete(`/family-trees/${treeId}/events/${id}`); toast.success("Deleted"); onChanged?.(); }
    catch (e) { toast.error("Failed"); }
  };
  const notify = async (id) => {
    try {
      const { data } = await http.post(`/family-trees/${treeId}/events/${id}/notify`);
      toast.success(`Reminder sent to ${data.sent_to} member(s).`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
  };
  if (events.length === 0) return <div className="wofy-card text-sm text-center" style={{ color: "var(--wofy-text-soft)" }}>No family events yet.</div>;

  // Only show category chips that have at least one event.
  const cats = categoriesWithEvents(events);
  const filtered = activeCat === "all"
    ? events
    : events.filter((e) => getEventMeta(e.event_type)?.categoryId === activeCat);

  return (
    <div className="space-y-3">
      {/* Category filter tabs — only categories with posted events. */}
      {cats.length > 1 && (
        <div className="flex gap-2 flex-wrap" data-testid="ft-events-categories">
          <button
            type="button"
            onClick={() => setActiveCat("all")}
            data-testid="ft-events-cat-all"
            className="text-xs px-3 py-1 rounded-full border inline-flex items-center gap-1.5"
            style={{
              borderColor: activeCat === "all" ? "var(--wofy-primary)" : "var(--wofy-border)",
              background: activeCat === "all" ? "var(--wofy-primary)" : "transparent",
              color: activeCat === "all" ? "white" : "var(--wofy-text)",
            }}
          >
            All <span className="opacity-70 font-mono">{events.length}</span>
          </button>
          {cats.map((c) => {
            const count = events.filter((e) => getEventMeta(e.event_type)?.categoryId === c.id).length;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setActiveCat(c.id)}
                data-testid={`ft-events-cat-${c.id}`}
                className="text-xs px-3 py-1 rounded-full border inline-flex items-center gap-1.5"
                style={{
                  borderColor: activeCat === c.id ? "var(--wofy-primary)" : "var(--wofy-border)",
                  background: activeCat === c.id ? "var(--wofy-primary)" : "transparent",
                  color: activeCat === c.id ? "white" : "var(--wofy-text)",
                }}
              >
                <span aria-hidden>{c.icon}</span>
                <span>{c.label}</span>
                <span className="opacity-70 font-mono">{count}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
      {filtered.map((e) => {
        const meta = getEventMeta(e.event_type);
        return (
        <div key={e.id} className="wofy-card" data-testid={`ft-event-card-${e.id}`}>
          <div className="flex items-baseline justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wider font-mono inline-flex items-center gap-1.5" style={{ color: "var(--wofy-primary)" }}>
                <span aria-hidden className="text-base leading-none">{meta.icon}</span>
                <span>{meta.label}</span>
                <span className="opacity-60">·</span>
                <span className="opacity-70">{meta.categoryLabel}</span>
              </div>
              <h3 className="font-display text-lg mt-0.5">{e.title}</h3>
              <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
                {new Date(e.event_date).toLocaleDateString(undefined, { dateStyle: "full" })}
                {e.location && ` · ${e.location}`}
              </div>
            </div>
            {isAdmin && <button onClick={() => del(e.id)} className="opacity-50 hover:opacity-100"><Trash2 className="w-4 h-4" style={{ color: "#B23A3A" }} /></button>}
          </div>
          {e.description && <p className="mt-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>{e.description}</p>}
          <div className="mt-3 text-xs flex items-center gap-3">
            <span style={{ color: "var(--wofy-text-soft)" }}>
              Going: <b>{e.rsvp_summary?.yes || 0}</b> · Maybe: {e.rsvp_summary?.maybe || 0} · Not: {e.rsvp_summary?.no || 0}
            </span>
          </div>
          <div className="mt-2 flex gap-2 flex-wrap items-center">
            {["yes", "maybe", "no"].map((r) => (
              <button
                key={r}
                data-testid={`ft-rsvp-${e.id}-${r}`}
                onClick={() => rsvp(e.id, r)}
                className={`text-xs px-3 py-1 rounded-full border`}
                style={{
                  borderColor: e.rsvp_summary?.my_response === r ? "var(--wofy-primary)" : "var(--wofy-border)",
                  background: e.rsvp_summary?.my_response === r ? "var(--wofy-primary)" : "transparent",
                  color: e.rsvp_summary?.my_response === r ? "white" : "var(--wofy-text)",
                }}
              >
                {r === "yes" ? "Going" : r === "maybe" ? "Maybe" : "Can't make it"}
              </button>
            ))}
            {isAdmin && (
              <button
                onClick={() => notify(e.id)}
                data-testid={`ft-notify-${e.id}`}
                className="text-xs px-3 py-1 rounded-full border ml-auto inline-flex items-center gap-1"
                style={{ borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" }}
                title="Email a reminder to every tree member"
              >
                <Send className="w-3 h-3" /> Notify members
              </button>
            )}
          </div>
        </div>
        );
      })}
    </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Albums tab (Phase 2).                                              */
/* ------------------------------------------------------------------ */
function AlbumsTab({ treeId, isAdmin }) {
  const [albums, setAlbums] = useState([]);
  const [creating, setCreating] = useState(false);
  const [openAlbum, setOpenAlbum] = useState(null);
  const load = async () => {
    try {
      const { data } = await http.get(`/family-trees/${treeId}/albums`);
      setAlbums(data.items);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [treeId]);
  const del = async (aid) => {
    if (!window.confirm("Delete this album and every photo inside?")) return;
    try { await http.delete(`/family-trees/${treeId}/albums/${aid}`); toast.success("Album deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <div data-testid="ft-albums-tab">
      {isAdmin && (
        <div className="flex justify-end mb-3">
          <button onClick={() => setCreating(true)} className="wofy-btn-primary" data-testid="ft-album-new">
            <Plus className="w-4 h-4" /> New album
          </button>
        </div>
      )}
      {albums.length === 0 ? (
        <div className="wofy-card text-sm text-center" style={{ color: "var(--wofy-text-soft)" }}>
          No albums yet. Photo albums are a warm way to gather milestones — weddings, reunions, family holidays.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {albums.map((a) => (
            <div key={a.id} className="wofy-card cursor-pointer" onClick={() => setOpenAlbum(a)} data-testid={`ft-album-${a.id}`}>
              {a.cover_photo ? (
                <img src={resolveImage(a.cover_photo)} alt="" className="w-full h-40 object-cover rounded-lg mb-3" />
              ) : (
                <div className="w-full h-40 rounded-lg mb-3 flex items-center justify-center" style={{ background: "var(--wofy-surface)" }}>
                  <ImageIcon className="w-8 h-8" style={{ color: "var(--wofy-text-soft)" }} />
                </div>
              )}
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display text-lg leading-snug">{a.title}</div>
                  {a.description && <p className="text-xs mt-1 line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>{a.description}</p>}
                  <div className="mt-2 text-xs" style={{ color: "var(--wofy-text-soft)" }}>{a.photo_count || 0} photo{a.photo_count === 1 ? "" : "s"}</div>
                </div>
                {isAdmin && (
                  <button onClick={(e) => { e.stopPropagation(); del(a.id); }} className="opacity-50 hover:opacity-100"><Trash2 className="w-4 h-4" style={{ color: "#B23A3A" }} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {creating && <NewAlbumModal treeId={treeId} onClose={() => setCreating(false)} onCreated={load} />}
      {openAlbum && <AlbumViewer treeId={treeId} album={openAlbum} onClose={() => { setOpenAlbum(null); load(); }} />}
    </div>
  );
}

function NewAlbumModal({ treeId, onClose, onCreated }) {
  const [f, setF] = useState({ title: "", description: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Give the album a title");
    setBusy(true);
    try { await http.post(`/family-trees/${treeId}/albums`, f); toast.success("Album created"); onClose(); onCreated?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#14213D]/25 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="wofy-card w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="ft-album-modal">
        <div className="overline mb-2">NEW ALBUM</div>
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="ft-album-title" className="wofy-input" placeholder="Album title *" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required />
          <textarea className="wofy-input" placeholder="Description (optional)" rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="wofy-btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} data-testid="ft-album-create" className="wofy-btn-primary">{busy ? "Creating…" : "Create album"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AlbumViewer({ treeId, album, onClose }) {
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const { data } = await http.get(`/family-trees/${treeId}/albums/${album.id}/photos`);
      setPhotos(data.items);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [album.id]);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Photo must be < 3 MB");
    const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeId}/albums/${album.id}/photos`, { image: dataUrl, caption });
      toast.success("Photo added");
      setCaption("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const del = async (pid) => {
    if (!window.confirm("Delete this photo?")) return;
    try { await http.delete(`/family-trees/${treeId}/albums/${album.id}/photos/${pid}`); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#14213D]/30 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="wofy-card w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ft-album-viewer">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="overline">ALBUM</div>
            <h3 className="font-display text-2xl">{album.title}</h3>
            {album.description && <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>{album.description}</p>}
          </div>
          <button onClick={onClose} className="opacity-60 hover:opacity-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input
            className="wofy-input flex-1 text-sm"
            placeholder="Caption (optional)"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
          />
          <label className="wofy-btn-primary cursor-pointer" data-testid="ft-album-upload">
            <Plus className="w-4 h-4" /> {busy ? "Uploading…" : "Add photo"}
            <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={busy} />
          </label>
        </div>
        {photos.length === 0 ? (
          <div className="text-sm text-center py-8" style={{ color: "var(--wofy-text-soft)" }}>No photos yet — add the first memory.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {photos.map((p) => (
              <div key={p.id} className="relative group" data-testid={`ft-photo-${p.id}`}>
                <img src={resolveImage(p.image)} alt={p.caption || ""} className="w-full aspect-square object-cover rounded-lg" />
                {p.caption && <div className="text-xs mt-1 line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>{p.caption}</div>}
                <button onClick={() => del(p.id)} className="absolute top-1 right-1 p-1 rounded-full bg-white/85 text-[#14213D] border border-[#14213D]/20 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Modals.                                                            */
/* ------------------------------------------------------------------ */
// ============================================================
// DateBlock — MUST be declared at module scope, NOT inside a parent
// component. Redeclaring it on every render was making React remount
// the <input> on every keystroke → focus loss + only 1 char accepted.
// ============================================================
function DateBlock({ label, mode, setMode, value, onValueChange, testid }) {
  return (
    <div className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
      <div className="text-xs font-mono uppercase tracking-wider mb-2" style={{ color: "var(--wofy-text-soft)" }}>{label}</div>
      <div className="flex gap-1 mb-2">
        {[["date", "Full date"], ["year", "Year only"], ["unknown", "Unknown"]].map(([v, l]) => (
          <button
            key={v}
            type="button"
            data-testid={`${testid}-mode-${v}`}
            onClick={() => setMode(v)}
            className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border"
            style={{
              borderColor: mode === v ? "var(--wofy-primary)" : "var(--wofy-border)",
              background: mode === v ? "var(--wofy-primary)" : "transparent",
              color: mode === v ? "white" : "var(--wofy-text-soft)",
            }}
          >{l}</button>
        ))}
      </div>
      {mode === "date" && (
        <input type="date" data-testid={`${testid}-date`} className="wofy-input" value={value} onChange={(e) => onValueChange(e.target.value)} />
      )}
      {mode === "year" && (
        <input
          type="text"
          inputMode="numeric"
          data-testid={`${testid}-year`}
          className="wofy-input"
          placeholder="e.g. 1952"
          maxLength={4}
          value={value}
          onChange={(e) => onValueChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
        />
      )}
      {mode === "unknown" && (
        <div className="text-xs italic" style={{ color: "var(--wofy-text-soft)" }}>Will be recorded as unknown.</div>
      )}
    </div>
  );
}

function MemberModal({ treeId, onClose, onSaved }) {
  const draftKey = `ft-draft-member-${treeId}`;
  // Restore draft (once) so accidental navigation / refresh doesn't wipe input.
  const initial = (() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return null;
  })();
  const [f, setF] = useState(initial?.f || {
    first_name: "", middle_name: "", last_name: "", nickname: "", other_names: "",
    gender: "", birth_date: "", birth_date_is_year_only: false,
    death_date: "", death_date_is_year_only: false,
    birth_place: "", bio: "", photo: "",
  });
  const [busy, setBusy] = useState(false);
  const [birthMode, setBirthMode] = useState(initial?.birthMode || "date"); // "date" | "year" | "unknown"
  const [deathMode, setDeathMode] = useState(initial?.deathMode || "unknown"); // "date" | "year" | "unknown"
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Auto-save draft on every change (skip photo — too large for localStorage).
  useEffect(() => {
    try {
      const { photo, ...rest } = f;
      const hasContent = Object.values(rest).some((v) => typeof v === "string" ? v.trim() : v);
      if (hasContent) {
        localStorage.setItem(draftKey, JSON.stringify({ f: rest, birthMode, deathMode }));
      }
    } catch { /* ignore quota */ }
  }, [f, birthMode, deathMode, draftKey]);

  const clearDraft = () => { try { localStorage.removeItem(draftKey); } catch { /* ignore */ } };
  const hasDraft = !!initial;

  const onPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Photo must be < 2 MB");
    const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
    upd("photo", dataUrl);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name.trim()) return toast.error("First name is required");
    // Normalise birth/death based on mode
    const payload = { ...f };
    if (birthMode === "unknown") { payload.birth_date = ""; payload.birth_date_is_year_only = false; }
    else if (birthMode === "year") { payload.birth_date_is_year_only = true; }
    else { payload.birth_date_is_year_only = false; }
    if (deathMode === "unknown") { payload.death_date = ""; payload.death_date_is_year_only = false; }
    else if (deathMode === "year") { payload.death_date_is_year_only = true; }
    else { payload.death_date_is_year_only = false; }
    // Validate year values
    const yearOk = (v) => /^\d{4}$/.test((v || "").trim());
    if (birthMode === "year" && payload.birth_date && !yearOk(payload.birth_date)) return toast.error("Birth year must be a 4-digit year");
    if (deathMode === "year" && payload.death_date && !yearOk(payload.death_date)) return toast.error("Death year must be a 4-digit year");
    setBusy(true);
    try {
      const { data } = await http.post(`/family-trees/${treeId}/members`, payload);
      if (data.memorial_slug) toast.success("Member added + memorial draft created.");
      else toast.success("Member added");
      clearDraft();
      onClose(); onSaved?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-[#14213D]/50 backdrop-blur-sm flex items-start justify-center p-4 pt-28 overflow-y-auto"
      onClick={onClose}
    >
      <div className="wofy-card w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="member-modal">
        <div className="overline mb-2">ADD MEMBER</div>
        <h3 className="font-display text-2xl mb-1">Who's joining the tree?</h3>
        {hasDraft && (
          <div className="mb-3 text-[11px] flex items-center justify-between gap-2 px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)", background: "rgba(139,92,246,0.06)" }}>
            <span>Draft restored — your previous input was saved locally.</span>
            <button
              type="button"
              onClick={() => {
                clearDraft();
                setF({ first_name: "", middle_name: "", last_name: "", nickname: "", other_names: "", gender: "", birth_date: "", birth_date_is_year_only: false, death_date: "", death_date_is_year_only: false, birth_place: "", bio: "", photo: "" });
                setBirthMode("date"); setDeathMode("unknown");
              }}
              className="underline opacity-80 hover:opacity-100"
              data-testid="mm-clear-draft"
            >Clear draft</button>
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Full Names</label>
            <div className="grid grid-cols-3 gap-3 mt-1">
              <input data-testid="mm-first-name" className="wofy-input" placeholder="First name *" value={f.first_name} onChange={(e) => upd("first_name", e.target.value)} required />
              <input data-testid="mm-middle-name" className="wofy-input" placeholder="Middle name" value={f.middle_name} onChange={(e) => upd("middle_name", e.target.value)} />
              <input data-testid="mm-last-name" className="wofy-input" placeholder="Last name" value={f.last_name} onChange={(e) => upd("last_name", e.target.value)} />
            </div>
          </div>
          <TranslatableInput
            label="Other Names / Aliases"
            as="input"
            testid="mm-other-names"
            placeholder="e.g. maiden name, nickname, native-script spelling"
            value={f.other_names}
            onChange={(v) => upd("other_names", v)}
            inputCls="!py-2"
          />
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="mm-gender" className="wofy-input" value={f.gender} onChange={(e) => upd("gender", e.target.value)}>
              <option value="">Gender…</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
            </select>
            <input className="wofy-input" placeholder="Birthplace (optional)" value={f.birth_place} onChange={(e) => upd("birth_place", e.target.value)} />
          </div>
          <DateBlock label="Date of Birth" mode={birthMode} setMode={setBirthMode} value={f.birth_date} onValueChange={(v) => upd("birth_date", v)} testid="mm-birth" />
          <DateBlock label="Date of Death (leave unknown if living)" mode={deathMode} setMode={setDeathMode} value={f.death_date} onValueChange={(v) => upd("death_date", v)} testid="mm-death" />
          <div>
            <TranslatableInput
              label="Short Bio (optional)"
              as="textarea"
              rows={3}
              testid="mm-bio"
              placeholder="A few lines about them — you can add translations after typing."
              value={f.bio}
              onChange={(v) => upd("bio", v)}
            />
            {typeof f.bio === "string" && f.bio.trim().length >= 10 && (
              <div className="mt-1.5">
                <AIImproveButton
                  value={f.bio}
                  onImproved={(t) => upd("bio", t)}
                  kind="bio"
                  testId="mm-bio-ai"
                />
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Photo</label>
            <input type="file" accept="image/*" onChange={onPhoto} className="mt-1 block text-xs" data-testid="mm-photo" />
            {f.photo && <img src={f.photo} alt="" className="mt-2 w-20 h-20 rounded-lg object-cover" />}
          </div>
          {deathMode !== "unknown" && f.death_date && (
            <div className="text-xs p-2 rounded border" style={{ borderColor: "#5B4A73", background: "rgba(139,92,246,0.05)", color: "#5B4A73" }}>
              <Flower2 className="w-3 h-3 inline mr-1" /> A draft memorial will be created in Celebrate a Life — you can enrich and publish it later.
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="wofy-btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} data-testid="mm-save" className="wofy-btn-primary">{busy ? "Saving…" : "Add member"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RelationshipModal({ treeId, members, onClose, onSaved }) {
  const [f, setF] = useState({ person1_id: "", person2_id: "", type: "parent", wedding_date: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.person1_id || !f.person2_id || f.person1_id === f.person2_id) return toast.error("Pick two different people");
    setBusy(true);
    try {
      const payload = {
        person1_id: f.person1_id,
        person2_id: f.person2_id,
        type: f.type,
      };
      if (f.type === "spouse" && f.wedding_date) payload.wedding_date = f.wedding_date;
      await http.post(`/family-trees/${treeId}/relationships`, payload);
      toast.success("Linked");
      onClose(); onSaved?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const nm = (m) => [m.first_name, m.last_name].filter(Boolean).join(" ");
  return (
    <div className="fixed inset-0 z-50 bg-[#14213D]/25 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="wofy-card w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="rel-modal">
        <div className="overline mb-2">LINK RELATIONSHIP</div>
        <form onSubmit={submit} className="space-y-3">
          <label className="block text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
            Relationship
            <select className="wofy-input mt-1 normal-case tracking-normal" value={f.type} onChange={(e) => upd("type", e.target.value)}>
              <option value="parent">Parent → Child</option>
              <option value="spouse">Spouse ↔ Spouse</option>
              <option value="sibling">Sibling ↔ Sibling</option>
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
              {f.type === "parent" ? "Parent" : "Person A"}
              <select data-testid="rel-p1" className="wofy-input mt-1 normal-case tracking-normal" value={f.person1_id} onChange={(e) => upd("person1_id", e.target.value)}>
                <option value="">Select…</option>
                {members.map((m) => <option key={m.id} value={m.id}>{nm(m)}</option>)}
              </select>
            </label>
            <label className="block text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
              {f.type === "parent" ? "Child" : "Person B"}
              <select data-testid="rel-p2" className="wofy-input mt-1 normal-case tracking-normal" value={f.person2_id} onChange={(e) => upd("person2_id", e.target.value)}>
                <option value="">Select…</option>
                {members.map((m) => <option key={m.id} value={m.id}>{nm(m)}</option>)}
              </select>
            </label>
          </div>
          {f.type === "spouse" && (
            <label className="block text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
              Wedding date (optional)
              <input
                type="date"
                data-testid="rel-wedding-date"
                className="wofy-input mt-1 normal-case tracking-normal"
                value={f.wedding_date}
                onChange={(e) => upd("wedding_date", e.target.value)}
              />
            </label>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="wofy-btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} className="wofy-btn-primary" data-testid="rel-save">{busy ? "Linking…" : "Link"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EventModal({ treeId, onClose, onSaved }) {
  const [f, setF] = useState({ title: "", event_type: "birthday", event_date: "", location: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeCat, setActiveCat] = useState(EVENT_CATEGORIES[0].id);
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.event_date) return toast.error("Title and date required");
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeId}/events`, f);
      toast.success("Event created");
      onClose(); onSaved?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const selectedMeta = getEventMeta(f.event_type);
  return (
    <div className="fixed inset-0 z-50 bg-[#14213D]/25 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="wofy-card w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="event-modal">
        <div className="overline mb-2">NEW FAMILY EVENT</div>
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="ev-title" className="wofy-input" placeholder="Title *" value={f.title} onChange={(e) => upd("title", e.target.value)} required />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <button
                type="button"
                onClick={() => setPickerOpen((v) => !v)}
                data-testid="ev-type-picker"
                className="w-full wofy-input inline-flex items-center justify-between gap-2"
                style={{ textAlign: "left" }}
              >
                <span className="inline-flex items-center gap-2 truncate">
                  <span aria-hidden className="text-lg leading-none">{selectedMeta.icon}</span>
                  <span className="truncate">{selectedMeta.label}</span>
                </span>
                <ChevronDown className="w-4 h-4 shrink-0" />
              </button>
            </div>
            <input type="date" data-testid="ev-date" className="wofy-input" value={f.event_date} onChange={(e) => upd("event_date", e.target.value)} required />
          </div>

          {pickerOpen && (
            <div className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }} data-testid="ev-type-panel">
              {/* Category tabs */}
              <div className="flex gap-1 flex-wrap mb-3">
                {EVENT_CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setActiveCat(c.id)}
                    data-testid={`ev-cat-${c.id}`}
                    className="text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1"
                    style={{
                      borderColor: activeCat === c.id ? "var(--wofy-primary)" : "var(--wofy-border)",
                      background: activeCat === c.id ? "var(--wofy-primary)" : "transparent",
                      color: activeCat === c.id ? "white" : "var(--wofy-text)",
                    }}
                  >
                    <span aria-hidden>{c.icon}</span>
                    <span>{c.label}</span>
                  </button>
                ))}
              </div>
              {/* Items in the active category */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-56 overflow-y-auto">
                {EVENT_CATEGORIES.find((c) => c.id === activeCat).items.map((it) => (
                  <button
                    key={it.value}
                    type="button"
                    onClick={() => { upd("event_type", it.value); setPickerOpen(false); }}
                    data-testid={`ev-type-${it.value}`}
                    className="text-left text-xs px-2 py-1.5 rounded border inline-flex items-center gap-2 hover:bg-white"
                    style={{
                      borderColor: f.event_type === it.value ? "var(--wofy-primary)" : "var(--wofy-border)",
                      background: f.event_type === it.value ? "rgba(139,92,246,0.08)" : "transparent",
                    }}
                  >
                    <span aria-hidden className="text-base leading-none">{it.icon}</span>
                    <span className="truncate">{it.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <input className="wofy-input" placeholder="Location (optional)" value={f.location} onChange={(e) => upd("location", e.target.value)} />
          <textarea className="wofy-input" placeholder="Description" rows={3} value={f.description} onChange={(e) => upd("description", e.target.value)} />
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="wofy-btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} data-testid="ev-save" className="wofy-btn-primary">{busy ? "Creating…" : "Create"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Family Chat tab (Phase 3)                                          */
/* ------------------------------------------------------------------ */
function FamilyChatTab({ treeId, user, tree, isAdmin }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const canPost = isAdmin || (user && (tree.member_user_ids || []).includes(user.id));

  const load = async () => {
    try {
      const { data } = await http.get(`/family-trees/${treeId}/messages`);
      setMessages(data.items);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load messages");
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [treeId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeId}/messages`, { body: body.trim() });
      setBody("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const del = async (mid) => {
    if (!window.confirm("Delete this message?")) return;
    try { await http.delete(`/family-trees/${treeId}/messages/${mid}`); load(); }
    catch (e) { toast.error("Failed"); }
  };

  if (!user) return (
    <div className="wofy-card text-sm text-center" style={{ color: "var(--wofy-text-soft)" }}>
      Sign in to view the family chat.
    </div>
  );
  if (error) return (
    <div className="wofy-card text-sm text-center" style={{ color: "var(--wofy-text-soft)" }}>
      {error} <br/>
      <span className="text-xs">Family chat is available to tree admins and members. Ask an admin to invite you.</span>
    </div>
  );

  return (
    <div data-testid="ft-chat-tab" className="wofy-card">
      <div className="max-h-[500px] overflow-y-auto space-y-3 mb-4 pr-2">
        {messages.length === 0 ? (
          <p className="text-sm text-center py-8" style={{ color: "var(--wofy-text-soft)" }}>
            No messages yet — say hello to your family.
          </p>
        ) : messages.map((m) => (
          <div key={m.id} data-testid={`ft-msg-${m.id}`} className={`flex ${m.author_id === user.id ? "justify-end" : "justify-start"}`}>
            <div
              className="max-w-[75%] rounded-2xl px-4 py-2 text-sm"
              style={{
                background: m.author_id === user.id ? "var(--wofy-primary)" : "var(--wofy-surface)",
                color: m.author_id === user.id ? "white" : "var(--wofy-text)",
              }}
            >
              <div className="text-[10px] font-mono uppercase tracking-wider mb-1 opacity-80">
                {m.author_name} · {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
              <p className="whitespace-pre-wrap break-words">{m.body}</p>
              {(m.author_id === user.id || isAdmin) && (
                <button onClick={() => del(m.id)} className="mt-1 text-[10px] opacity-60 hover:opacity-100 underline">delete</button>
              )}
            </div>
          </div>
        ))}
      </div>
      {canPost ? (
        <form onSubmit={submit} className="flex gap-2 pt-3 border-t" style={{ borderColor: "var(--wofy-line)" }}>
          <input
            data-testid="ft-chat-input"
            className="wofy-input flex-1"
            placeholder="Message your family…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={4000}
          />
          <button type="submit" disabled={busy} data-testid="ft-chat-send" className="wofy-btn-primary">
            <Send className="w-4 h-4" />
          </button>
        </form>
      ) : (
        <div className="text-xs italic text-center pt-3 border-t" style={{ color: "var(--wofy-text-soft)", borderColor: "var(--wofy-line)" }}>
          Only tree members can post. Ask an admin to invite you.
        </div>
      )}
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Share modal — copy link + WhatsApp/email share buttons             */
/* ------------------------------------------------------------------ */
function ShareModal({ tree, onClose }) {
  const [preview, setPreview] = useState(null);
  const url = typeof window !== "undefined"
    ? `${window.location.origin}/family/${tree.slug || tree.id}`
    : "";
  const shareText = `Explore "${tree.name}" — a family tree on Social Pulse`;

  useEffect(() => {
    http.get(`/family-trees/${tree.id}/share`).then((r) => setPreview(r.data)).catch(() => {});
  }, [tree.id]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch (e) { toast.error("Copy failed — long-press the link to copy"); }
  };
  const openWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText}\n${url}`)}`, "_blank");
  };
  const openEmail = () => {
    window.open(`mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(`I thought you'd love this: ${url}`)}`);
  };
  const openTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`, "_blank");
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#14213D]/25 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="wofy-card w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="ft-share-modal">
        <div className="flex items-start justify-between mb-2">
          <div className="overline">SHARE THIS FAMILY TREE</div>
          <button onClick={onClose} className="opacity-60 hover:opacity-100"><X className="w-5 h-5" /></button>
        </div>
        <h3 className="font-display text-2xl mb-1">{tree.name}</h3>
        <p className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
          {preview ? `${preview.member_count} member${preview.member_count === 1 ? "" : "s"} · ${preview.country || "Global"}` : "Loading preview…"}
        </p>
        {tree.visibility !== "public" && (
          <p className="mt-2 text-xs p-2 rounded" style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)" }}>
            This tree is <b>{tree.visibility}</b>. Recipients will see only the name and be asked to request access.
          </p>
        )}
        <div className="mt-4 flex items-center gap-2">
          <input readOnly value={url} className="wofy-input flex-1 text-xs" data-testid="ft-share-url" />
          <button onClick={copy} data-testid="ft-share-copy" className="wofy-btn-primary !px-3">
            <Copy className="w-4 h-4" />
          </button>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          <button onClick={openWhatsApp} data-testid="ft-share-whatsapp" className="wofy-btn-ghost text-xs">WhatsApp</button>
          <button onClick={openEmail} data-testid="ft-share-email" className="wofy-btn-ghost text-xs">Email</button>
          <button onClick={openTwitter} data-testid="ft-share-twitter" className="wofy-btn-ghost text-xs">Twitter/X</button>
        </div>
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* GEDCOM export / import                                             */
/* ------------------------------------------------------------------ */
function GedcomModal({ tree, isAdmin, onClose, onImported }) {
  const [busy, setBusy] = useState(false);
  const doExport = () => {
    // Force a browser download from the API endpoint with cookies.
    const link = document.createElement("a");
    link.href = `${API}/family-trees/${tree.id}/gedcom`;
    link.download = `${tree.slug || tree.id}.ged`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const doImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("GEDCOM file must be < 5 MB");
    const content = await file.text();
    setBusy(true);
    try {
      const { data } = await http.post(`/family-trees/${tree.id}/gedcom/import`, { content });
      toast.success(`Imported ${data.members_added} members, ${data.relationships_added} relationships`);
      onImported?.();
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#14213D]/25 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="wofy-card w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="ft-gedcom-modal">
        <div className="flex items-start justify-between mb-2">
          <div className="overline">GEDCOM · GENEALOGY EXCHANGE</div>
          <button onClick={onClose} className="opacity-60 hover:opacity-100"><X className="w-5 h-5" /></button>
        </div>
        <h3 className="font-display text-2xl mb-2">Move your tree in and out</h3>
        <p className="text-xs mb-4" style={{ color: "var(--wofy-text-soft)" }}>
          GEDCOM is the free, open standard used by Ancestry, MyHeritage, FamilySearch,
          RootsMagic, and every major genealogy program. Export to move your tree
          anywhere; import to bring an existing family tree into Social Pulse.
        </p>
        <div className="space-y-3">
          <button onClick={doExport} data-testid="ft-gedcom-export" className="wofy-btn-primary w-full justify-center">
            <Download className="w-4 h-4" /> Export {tree.name}
          </button>
          {isAdmin ? (
            <label className={`wofy-btn-ghost w-full justify-center cursor-pointer ${busy ? "opacity-50" : ""}`} data-testid="ft-gedcom-import-label">
              <Upload className="w-4 h-4" /> {busy ? "Importing…" : "Import a .ged file"}
              <input type="file" accept=".ged,text/plain,application/x-gedcom" className="hidden" onChange={doImport} disabled={busy} data-testid="ft-gedcom-import" />
            </label>
          ) : (
            <p className="text-xs text-center italic" style={{ color: "var(--wofy-text-soft)" }}>
              Only tree admins can import.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* InviteTab — lets any tree member send an invitation to a relative   */
/* they want to join the tree. Two channels:                          */
/*   1. Shareable invite link (copy / WhatsApp / email deep-link)     */
/*   2. Direct invite form (name + email or phone + personal note)    */
/* ------------------------------------------------------------------ */
function InviteTab({ treeId, treeName, isAdmin, user }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState([]);      // list of accepted invitation payloads
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const shareUrl = `${window.location.origin}/family/${treeId}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Invite link copied — paste it wherever you'd like.");
    } catch {
      toast.error("Couldn't copy — long-press the link and copy manually.");
    }
  };

  const openWhatsApp = () => {
    const text = encodeURIComponent(
      `${user?.name || "I"} invited you to join our family tree "${treeName}" on Social Pulse: ${shareUrl}`,
    );
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
  };

  const openMail = () => {
    const subject = encodeURIComponent(`You're invited to join "${treeName}" on Social Pulse`);
    const body = encodeURIComponent(
      `Hi${f.name ? " " + f.name : ""},\n\n` +
      `I'd love for you to join our family tree "${treeName}" on Social Pulse.\n\n` +
      `${f.note ? f.note + "\n\n" : ""}` +
      `Open this link to view and join:\n${shareUrl}\n\n— ${user?.name || "A family member"}`,
    );
    const to = f.email ? encodeURIComponent(f.email) : "";
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Add the person's name so they know it's from you.");
    if (!f.email.trim() && !f.phone.trim()) return toast.error("Add an email or a phone number.");
    setBusy(true);
    try {
      const { data } = await http.post(`/family-trees/${treeId}/invitations`, {
        name: f.name.trim(),
        email: f.email.trim() || null,
        phone: f.phone.trim() || null,
        note: f.note.trim() || null,
      });
      setSent((prev) => [{ ...data, ts: Date.now() }, ...prev].slice(0, 20));
      setF({ name: "", email: "", phone: "", note: "" });
      toast.success(`Invitation queued for ${data.name || f.name}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't queue the invitation.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="invite-tab">
      {/* Shareable invite link */}
      <div className="wofy-card">
        <div className="overline mb-2">SHAREABLE INVITE LINK</div>
        <p className="text-sm mb-3" style={{ color: "var(--wofy-text-soft)" }}>
          Anyone with this link lands on the tree's public card and can request access — perfect for family WhatsApp groups.
        </p>
        <div className="flex flex-col md:flex-row gap-2 mb-3">
          <input
            readOnly
            value={shareUrl}
            className="wofy-input flex-1"
            onFocus={(e) => e.target.select()}
            data-testid="invite-share-url"
          />
          <div className="flex gap-2">
            <button onClick={copyLink} className="wofy-btn-primary" data-testid="invite-copy-link">
              <Copy className="w-4 h-4" /> Copy
            </button>
            <button onClick={openWhatsApp} className="wofy-btn-ghost" data-testid="invite-whatsapp">
              WhatsApp
            </button>
            <button onClick={openMail} className="wofy-btn-ghost" data-testid="invite-email">
              Email
            </button>
          </div>
        </div>
      </div>

      {/* Direct invite form */}
      <form onSubmit={submit} className="wofy-card space-y-3" data-testid="invite-form">
        <div>
          <div className="overline">INVITE SOMEONE DIRECTLY</div>
          <p className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            We'll log this in the family invitations list so admins can follow up.
          </p>
        </div>
        <label className="block">
          <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>Name *</div>
          <input required value={f.name} onChange={(e) => upd("name", e.target.value)} className="wofy-input" placeholder="e.g. Cousin Anita" data-testid="invite-name" />
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>Email</div>
            <input type="email" value={f.email} onChange={(e) => upd("email", e.target.value)} className="wofy-input" placeholder="anita@example.com" data-testid="invite-email-input" />
          </label>
          <label className="block">
            <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>Phone</div>
            <input type="tel" value={f.phone} onChange={(e) => upd("phone", e.target.value)} className="wofy-input" placeholder="+91 98…" data-testid="invite-phone-input" />
          </label>
        </div>
        <label className="block">
          <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>Personal note (optional)</div>
          <textarea rows={3} value={f.note} onChange={(e) => upd("note", e.target.value)} className="wofy-input" placeholder="Hi Anita — Uncle Saji here. Would love to have you on our family tree…" maxLength={800} data-testid="invite-note" />
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={openMail} className="wofy-btn-ghost" data-testid="invite-mail-compose">
            Compose in email
          </button>
          <button type="submit" disabled={busy} className="wofy-btn-primary disabled:opacity-60" data-testid="invite-submit">
            {busy ? "Sending…" : "Send invitation"}
          </button>
        </div>
      </form>

      {sent.length > 0 && (
        <div className="wofy-card">
          <div className="overline mb-2">RECENTLY SENT (THIS SESSION)</div>
          <ul className="space-y-2 text-sm">
            {sent.map((s) => (
              <li key={s.ts} className="flex items-center justify-between border-b pb-1" style={{ borderColor: "var(--wofy-border)" }} data-testid={`invite-sent-${s.ts}`}>
                <span><b>{s.name}</b> · {s.email || s.phone || "no contact"}</span>
                <span className="text-[10px] font-mono uppercase opacity-60">Queued</span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] mt-2 opacity-70">
            Admins see the full list in the Family Management Center.
          </p>
        </div>
      )}
    </div>
  );
}





/* ------------------------------------------------------------------ */
/* Request Access landing — shown to non-members visiting an invite    */
/* tree. Displays the tree's public card + a structured form so tree  */
/* admins can vet the requester before granting access.                */
/* ------------------------------------------------------------------ */
function RequestAccessLanding({ card, user }) {
  const [tab, setTab] = useState("request");
  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="ra-landing">
      <Link to="/family" className="text-xs underline" style={{ color: "var(--wofy-primary)" }}>← Back to family trees</Link>

      {/* Tree card hero */}
      <div className="mt-3 rounded-2xl overflow-hidden border" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }} data-testid="ra-tree-card">
        {card.cover_image ? (
          <div className="h-40 md:h-56 w-full bg-cover bg-center" style={{ backgroundImage: `url(${card.cover_image})` }} />
        ) : (
          <div className="h-32 md:h-40 w-full flex items-center justify-center" style={{ background: "linear-gradient(120deg,#f3e8ff 0%,#fce7f3 40%,#dbeafe 100%)" }}>
            <Users className="w-10 h-10" style={{ color: "var(--wofy-primary)" }} />
          </div>
        )}
        <div className="p-5">
          <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>{(card.visibility || "").toUpperCase()} · REQUEST ACCESS</div>
          <h1 className="font-display text-3xl md:text-4xl leading-tight" data-testid="ra-tree-name">{card.name}</h1>
          <div className="mt-2 text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
            {card.country || "Global"} · {card.member_count} member{card.member_count === 1 ? "" : "s"}
            {card.owner_name && <> · Owner: {card.owner_name}</>}
          </div>
          {card.description && (
            <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--wofy-text-soft)" }}>{card.description}</p>
          )}
        </div>
      </div>

      {/* Tabs — currently one, but the pattern scales when we add more   */}
      {/* (e.g. Public Members preview for public trees, About, etc.).    */}
      <div className="flex gap-2 mt-6 mb-4 border-b overflow-x-auto" style={{ borderColor: "var(--wofy-line)" }}>
        {[
          { v: "request", label: "Request Access", icon: UserPlus },
        ].map(({ v, label, icon: Icon }) => (
          <button
            key={v}
            data-testid={`ra-tab-${v}`}
            onClick={() => setTab(v)}
            className="px-3 py-2 text-sm border-b-2 -mb-px inline-flex items-center gap-1.5 whitespace-nowrap"
            style={{ borderColor: tab === v ? "var(--wofy-primary)" : "transparent", color: tab === v ? "var(--wofy-primary)" : "var(--wofy-text-soft)" }}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "request" && (
        user ? (
          <RequestAccessForm treeId={card.id} treeName={card.name} user={user} />
        ) : (
          <div className="rounded-lg border p-6 text-center" style={{ borderColor: "var(--wofy-border)" }} data-testid="ra-signin-gate">
            <p className="text-sm mb-3">Please sign in first so the family admin knows who's asking.</p>
            <Link to="/login" className="wofy-btn-primary inline-flex">Sign in to continue</Link>
          </div>
        )
      )}
    </div>
  );
}

function RequestAccessForm({ treeId, treeName, user }) {
  // Auto-populate location from LocalityContext (browser geoloc / IP fallback
  // already ran globally at app boot, so this is usually pre-filled).
  const { locality } = useLocality();
  const autoLoc = locality
    ? [locality.city, locality.state, locality.country].filter(Boolean).join(", ")
    : "";

  const [f, setF] = useState({
    name: user?.name || "",
    phone: user?.phone_number || "",
    email: user?.email || "",
    location: autoLoc,
    reason: "",
    comments: "",
  });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Re-populate location once the locality context resolves (async on first
  // page load).
  useEffect(() => {
    if (!f.location && autoLoc) upd("location", autoLoc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoc]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Please tell them your name");
    if (!f.reason.trim()) return toast.error("Please share your reason for requesting access");
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeId}/access-request`, {
        name: f.name.trim(),
        phone: f.phone.trim() || null,
        email: f.email.trim() || null,
        location: f.location.trim() || null,
        reason: f.reason.trim(),
        comments: f.comments.trim() || null,
        // Legacy `message` mirror so older admin dashboards keep working.
        message: f.reason.trim() + (f.comments.trim() ? `\n\n${f.comments.trim()}` : ""),
      });
      setSent(true);
      toast.success(`Request sent to the ${treeName} admins — they'll review it soon.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit request");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-lg border p-6" style={{ borderColor: "var(--wofy-border)", background: "rgba(16,185,129,0.05)" }} data-testid="ra-sent">
        <div className="overline mb-1" style={{ color: "#059669" }}>✓ REQUEST SUBMITTED</div>
        <h3 className="font-display text-2xl">Thanks — your request is on its way.</h3>
        <p className="text-sm mt-2" style={{ color: "var(--wofy-text-soft)" }}>
          An admin of <b>{treeName}</b> will review your details and get back to you. If your request is denied, you can re-apply after 7 days.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-lg border p-5 md:p-6 space-y-4" style={{ borderColor: "var(--wofy-border)" }} data-testid="ra-form">
      <div>
        <div className="overline mb-1">TELL THEM WHO YOU ARE</div>
        <p className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
          The family admin will see this. Only share what you're comfortable with.
        </p>
      </div>

      <label className="block">
        <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>Your name *</div>
        <input
          required
          value={f.name}
          onChange={(e) => upd("name", e.target.value)}
          className="wofy-input"
          placeholder="e.g. Anita Kurisummoottil"
          data-testid="ra-name"
        />
      </label>

      <label className="block">
        <div className="text-xs font-mono uppercase tracking-wider mb-1 flex items-center gap-2" style={{ color: "var(--wofy-text-soft)" }}>
          <span>Location</span>
          {autoLoc && <span className="normal-case tracking-normal opacity-70 text-[10px]">(auto-detected — edit if needed)</span>}
        </div>
        <input
          value={f.location}
          onChange={(e) => upd("location", e.target.value)}
          className="wofy-input"
          placeholder="City, State, Country"
          data-testid="ra-location"
        />
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>Phone number</div>
          <input
            type="tel"
            value={f.phone}
            onChange={(e) => upd("phone", e.target.value)}
            className="wofy-input"
            placeholder="+91 98…"
            data-testid="ra-phone"
          />
        </label>
        <label className="block">
          <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>Email</div>
          <input
            type="email"
            value={f.email}
            onChange={(e) => upd("email", e.target.value)}
            className="wofy-input"
            placeholder="you@example.com"
            data-testid="ra-email"
          />
        </label>
      </div>

      <label className="block">
        <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>Your reason to request access *</div>
        <input
          required
          value={f.reason}
          onChange={(e) => upd("reason", e.target.value)}
          className="wofy-input"
          placeholder="e.g. I'm Saji's cousin from Kochi — I'd like to view family history"
          maxLength={200}
          data-testid="ra-reason"
        />
      </label>

      <label className="block">
        <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>Comments</div>
        <textarea
          rows={4}
          value={f.comments}
          onChange={(e) => upd("comments", e.target.value)}
          className="wofy-input"
          placeholder="Anything else the admin should know (relationship, family branch, mutual relatives, etc.)"
          maxLength={1000}
          data-testid="ra-comments"
        />
      </label>

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="submit"
          disabled={busy}
          className="wofy-btn-primary disabled:opacity-60"
          data-testid="ra-submit"
        >
          {busy ? "Sending…" : "Send request"}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* Family Timeline — every birth/death/wedding/event on one horizontal
/* ribbon. Auto-generated from members + relationships + events.      */
/* ------------------------------------------------------------------ */
function FamilyTimeline({ members, rels, events }) {
  const items = useMemo(() => {
    const out = [];
    const nameOf = (id) => {
      const m = members.find((x) => x.id === id);
      return m ? [m.first_name, m.last_name].filter(Boolean).join(" ") : "Unknown";
    };
    const yearOf = (dateStr, yearOnly) => {
      if (!dateStr) return null;
      const s = String(dateStr);
      if (yearOnly || s.length === 4) {
        const y = parseInt(s.slice(0, 4), 10);
        return Number.isFinite(y) ? y : null;
      }
      const y = parseInt(s.slice(0, 4), 10);
      return Number.isFinite(y) ? y : null;
    };

    for (const m of members) {
      const b = yearOf(m.birth_date, m.birth_date_is_year_only);
      const d = yearOf(m.death_date, m.death_date_is_year_only);
      const nm = [m.first_name, m.last_name].filter(Boolean).join(" ");
      if (b) out.push({
        year: b, exactDate: m.birth_date, type: "birth", icon: "\u{1F382}",
        title: `${nm} was born`, subtitle: m.birth_place || "", color: "#2C7A4B",
      });
      if (d) out.push({
        year: d, exactDate: m.death_date, type: "death", icon: "\u{1F54A}",
        title: `${nm} passed away`, subtitle: m.death_place || "", color: "#5B4A73",
        memorialSlug: m.memorial_slug,
      });
    }
    for (const r of rels) {
      if (r.type !== "spouse") continue;
      const wd = r.wedding_date || null;
      const y = yearOf(wd);
      if (!y) continue;
      out.push({
        year: y, exactDate: wd, type: "wedding", icon: "\u{1F48D}",
        title: `${nameOf(r.person1_id)} & ${nameOf(r.person2_id)} married`,
        subtitle: "", color: "#B25D38",
      });
    }
    for (const e of events) {
      const y = yearOf(e.event_date);
      if (!y) continue;
      const iconMap = { birthday: "\u{1F382}", wedding: "\u{1F48D}", funeral: "\u{1F54A}", reunion: "\u{1F389}" };
      out.push({
        year: y, exactDate: e.event_date, type: e.event_type || "event",
        icon: iconMap[e.event_type] || "\u{1F4C5}",
        title: e.title, subtitle: e.location || "", color: "var(--wofy-primary)",
      });
    }
    return out.sort((a, b) => a.year - b.year);
  }, [members, rels, events]);

  if (items.length === 0) {
    return (
      <div className="wofy-card text-center py-16" data-testid="ft-timeline-empty">
        <Clock className="w-6 h-6 mx-auto mb-2" style={{ color: "var(--wofy-primary)" }} />
        <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          The timeline fills in automatically as you add members, weddings and family events. Add a birth date to get started.
        </p>
      </div>
    );
  }

  const byDecade = new Map();
  for (const it of items) {
    const dec = Math.floor(it.year / 10) * 10;
    if (!byDecade.has(dec)) byDecade.set(dec, []);
    byDecade.get(dec).push(it);
  }
  const decades = [...byDecade.keys()].sort((a, b) => a - b);

  return (
    <div data-testid="ft-timeline-tab" className="overflow-x-auto">
      <div className="min-w-max pb-6">
        <div className="flex gap-8 relative">
          <div className="absolute left-0 right-0 top-6 h-0.5" style={{ background: "var(--wofy-line)" }} />
          {decades.map((dec) => (
            <div key={dec} className="relative flex flex-col items-start min-w-[220px] pt-2">
              <div className="flex items-center gap-2 mb-4 relative z-10">
                <div className="w-3 h-3 rounded-full" style={{ background: "var(--wofy-primary)" }} />
                <div className="font-mono text-sm font-bold tracking-wider" style={{ color: "var(--wofy-primary)" }}>
                  {dec}s
                </div>
              </div>
              <div className="space-y-3 pl-5">
                {byDecade.get(dec).map((it, idx) => (
                  <div
                    key={`${dec}-${idx}`}
                    data-testid={`ft-timeline-item-${it.year}-${it.type}`}
                    className="rounded-xl border p-3 text-xs"
                    style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-paper)", borderLeftWidth: 3, borderLeftColor: it.color }}
                  >
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg leading-none">{it.icon}</span>
                      <span className="font-mono font-bold" style={{ color: it.color }}>{it.year}</span>
                    </div>
                    <div className="mt-1 font-semibold">{it.title}</div>
                    {it.subtitle && <div className="text-[10px]" style={{ color: "var(--wofy-text-soft)" }}>{it.subtitle}</div>}
                    {it.memorialSlug && (
                      <Link to={`/celebrate/${it.memorialSlug}`} className="mt-1 inline-flex items-center gap-0.5 text-[10px] underline" style={{ color: "#5B4A73" }}>
                        <Flower2 className="w-3 h-3" /> Memorial
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <p className="text-[11px] italic text-center mt-2" style={{ color: "var(--wofy-text-soft)" }}>
        Scroll horizontally to travel through your family's history →
      </p>
    </div>
  );
}


function AccessTab({ treeId }) {
  const [reqs, setReqs] = useState(null);
  const [members, setMembers] = useState([]);
  const [claims, setClaims] = useState([]);
  const [pendingStories, setPendingStories] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get(`/family-trees/${treeId}/access-requests`);
      setReqs(data.items || []);
    } catch (e) {
      toast.error("Failed to load requests");
      setReqs([]);
    }
    try {
      const { data } = await http.get(`/family-trees/${treeId}/members-list`);
      setMembers(data.items || []);
    } catch { /* endpoint may not exist yet — soft-fail */ }
    try {
      const { data } = await http.get(`/family-trees/${treeId}/claims`, { params: { status: "pending" } });
      setClaims(data.claims || []);
    } catch { setClaims([]); }
    // Aggregate pending stories from every member in this tree.
    try {
      const { data } = await http.get(`/family-trees/${treeId}/members`);
      const items = [];
      for (const m of (data.items || [])) {
        for (const s of (m.stories || [])) {
          if (s.status === "pending") items.push({ ...s, member_id: m.id, member_name: [m.first_name, m.last_name].filter(Boolean).join(" ") });
        }
      }
      items.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setPendingStories(items);
    } catch { setPendingStories([]); }
  }, [treeId]);

  useEffect(() => { load(); }, [load]);

  const decide = async (rid, action) => {
    try {
      await http.post(`/family-trees/${treeId}/access-requests/${rid}/${action}`, { note: null });
      toast.success(`Request ${action}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const decideClaim = async (cid, action) => {
    try {
      await http.post(`/family-trees/${treeId}/claims/${cid}/${action}`);
      toast.success(`Claim ${action === "approve" ? "approved" : "denied"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const revoke = async (userId) => {
    if (!window.confirm("Revoke this member's access?")) return;
    try {
      await http.post(`/family-trees/${treeId}/access/${userId}/revoke`);
      toast.success("Access revoked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (reqs === null) return <p className="text-sm mt-6" style={{ color: "var(--wofy-text-soft)" }}>Loading…</p>;

  const pending = reqs.filter((r) => r.status === "pending");
  const history = reqs.filter((r) => r.status !== "pending").slice(0, 15);

  const decideStory = async (memberId, storyId, action) => {
    try {
      await http.post(`/family-trees/${treeId}/members/${memberId}/stories/${action}`, { story_id: storyId });
      toast.success(action === "approve" ? "Story approved" : "Story deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="mt-6 space-y-8" data-testid="access-tab">
      <section>
        <div className="overline mb-3">PENDING STORIES · {pendingStories.length}</div>
        {pendingStories.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>No family stories pending your review.</p>
        ) : (
          <ul className="space-y-3">
            {pendingStories.map((s) => (
              <li key={s.id} data-testid={`pending-story-${s.id}`} className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
                <div className="flex flex-wrap items-baseline gap-2 mb-1 text-sm">
                  <b>{s.title || "Untitled story"}</b>
                  {s.kind && <span className="text-[10px] font-mono uppercase opacity-60">{s.kind}</span>}
                  <span className="text-xs opacity-70">on <i>{s.member_name || "member"}</i></span>
                </div>
                <div className="text-[11px] font-mono opacity-60 mb-2">
                  by {s.contributor_name || "Someone"} · {new Date(s.created_at).toLocaleDateString()}
                </div>
                <div className="text-sm p-2 rounded" dir="auto" style={{ background: "var(--wofy-surface)", whiteSpace: "pre-wrap" }}>
                  {s.body}
                </div>
                <div className="flex gap-2 mt-2 justify-end">
                  <button onClick={() => decideStory(s.member_id, s.id, "approve")} className="wofy-btn-primary text-xs" data-testid={`story-approve-${s.id}`}>Approve</button>
                  <button onClick={() => decideStory(s.member_id, s.id, "delete")} className="wofy-btn-ghost text-xs" style={{ color: "#B23A3A" }} data-testid={`story-delete-${s.id}`}>Delete</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <div className="overline mb-3">MEMBER CLAIMS · PENDING · {claims.length}</div>
        {claims.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>No pending member claims.</p>
        ) : (
          <ul className="space-y-3">
            {claims.map((c) => (
              <li key={c.id} data-testid={`claim-row-${c.id}`} className="flex items-start gap-3 rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
                {c.user_avatar ? (
                  <img src={c.user_avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-display" style={{ background: "var(--wofy-surface)" }}>
                    {(c.user_name || "?").trim().slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm">
                    <b>{c.user_name || "Someone"}</b>
                    <span className="opacity-70"> wants to edit </span>
                    <b>{c.member_name}</b>
                  </div>
                  <div className="text-[11px] font-mono opacity-60">
                    {c.relationship && <span>Relation: {c.relationship} · </span>}
                    {new Date(c.created_at).toLocaleDateString()}
                  </div>
                  {c.note && <p className="mt-2 text-sm italic">"{c.note}"</p>}
                </div>
                <div className="flex flex-col gap-1.5">
                  <button onClick={() => decideClaim(c.id, "approve")} className="wofy-btn-primary text-xs" data-testid={`claim-approve-${c.id}`}>
                    Approve
                  </button>
                  <button onClick={() => decideClaim(c.id, "deny")} className="wofy-btn-ghost text-xs" style={{ color: "#B23A3A" }} data-testid={`claim-deny-${c.id}`}>
                    Deny
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <div className="overline mb-3">PENDING REQUESTS · {pending.length}</div>
        {pending.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>No pending access requests.</p>
        ) : (
          <ul className="space-y-3">
            {pending.map((r) => (
              <li key={r.id} data-testid={`access-req-${r.id}`} className="flex items-start gap-3 rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
                {r.user_avatar ? (
                  <img src={r.user_avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-display" style={{ background: "var(--wofy-surface)" }}>
                    {(r.user_name || "?").trim().slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">
                    {r.user_handle ? (
                      <Link to={`/u/${r.user_handle}`} className="hover:underline">{r.user_name || "Unknown"}</Link>
                    ) : (r.user_name || "Unknown")}
                  </div>
                  <div className="text-[11px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>
                    @{r.user_handle || "user"} · {new Date(r.created_at).toLocaleDateString()}
                  </div>
                  {r.message && <p className="mt-2 text-sm italic">"{r.message}"</p>}
                  {/* Structured requester profile — new form fields */}
                  {(r.requester_name || r.requester_phone || r.requester_email || r.requester_location || r.reason || r.comments) && (
                    <div className="mt-2 rounded-md border p-2 text-xs space-y-0.5" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
                      {r.requester_name && <div><span className="font-mono opacity-60 uppercase text-[10px] mr-1">Name</span>{r.requester_name}</div>}
                      {r.requester_location && <div><span className="font-mono opacity-60 uppercase text-[10px] mr-1">Location</span>{r.requester_location}</div>}
                      {r.requester_phone && <div><span className="font-mono opacity-60 uppercase text-[10px] mr-1">Phone</span><a href={`tel:${r.requester_phone}`} className="underline">{r.requester_phone}</a></div>}
                      {r.requester_email && <div><span className="font-mono opacity-60 uppercase text-[10px] mr-1">Email</span><a href={`mailto:${r.requester_email}`} className="underline">{r.requester_email}</a></div>}
                      {r.reason && <div><span className="font-mono opacity-60 uppercase text-[10px] mr-1">Reason</span>{r.reason}</div>}
                      {r.comments && <div className="mt-1 pt-1 border-t italic" style={{ borderColor: "var(--wofy-border)" }}>"{r.comments}"</div>}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <button
                    onClick={() => decide(r.id, "approve")}
                    data-testid={`access-approve-${r.id}`}
                    className="wofy-btn-primary text-xs"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => decide(r.id, "deny")}
                    data-testid={`access-deny-${r.id}`}
                    className="wofy-btn-ghost text-xs"
                    style={{ color: "#B23A3A" }}
                  >
                    Deny
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {members.length > 0 && (
        <section>
          <div className="overline mb-3">CURRENT MEMBERS · {members.length}</div>
          <ul className="space-y-2">
            {members.map((m) => (
              <li key={m.user_id} className="flex items-center gap-3 rounded-lg border p-2" style={{ borderColor: "var(--wofy-border)" }}>
                <div className="flex-1 text-sm">
                  <b>{m.user_name || m.user_id}</b>
                  <span className="ml-2 text-[11px] uppercase font-mono" style={{ color: "var(--wofy-text-soft)" }}>{m.role}</span>
                </div>
                {m.role !== "owner" && (
                  <button
                    onClick={() => revoke(m.user_id)}
                    className="wofy-btn-ghost text-xs"
                    style={{ color: "#B23A3A" }}
                    data-testid={`access-revoke-${m.user_id}`}
                  >
                    Revoke
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {history.length > 0 && (
        <section>
          <div className="overline mb-3">RECENT DECISIONS</div>
          <ul className="text-xs space-y-1" style={{ color: "var(--wofy-text-soft)" }}>
            {history.map((r) => (
              <li key={r.id}>
                {r.user_name || "Someone"} · <b>{r.status}</b> · {new Date(r.reviewed_at || r.created_at).toLocaleDateString()}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}



/* ------------------------------------------------------------------ */
/* Documents tab — read-only listing of tree PDFs (books, memoirs)    */
/* with an "Open book" link into the immersive FamilyBookViewer.      */
/* Uploads / deletes happen in Family Management Center → Documents.  */
/* ------------------------------------------------------------------ */
function DocumentsList({ treeSlug, isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.get(`/family-trees/${treeSlug}/documents`);
        if (!cancelled) setItems(data.items || []);
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [treeSlug]);

  if (loading) {
    return <div className="wofy-card text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  }
  if (items.length === 0) {
    return (
      <div className="wofy-card text-sm text-center" style={{ color: "var(--wofy-text-soft)" }} data-testid="ft-documents-empty">
        No documents yet. {isAdmin
          ? <Link className="underline" to={`/family/${treeSlug}/manage?tab=documents`}>Upload the family book →</Link>
          : "Ask an admin to upload the family book."}
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ft-documents-grid">
      {items.map((d) => (
        <Link
          key={d.id}
          to={`/family/${treeSlug}/book/${d.id}`}
          className="wofy-card overflow-hidden hover:shadow-lg transition"
          data-testid={`ft-document-${d.id}`}
        >
          <div className="aspect-[3/4] bg-gradient-to-br from-[#2d1e17] via-[#3a2a1e] to-[#1f1611] flex items-center justify-center overflow-hidden">
            <BookOpen className="w-20 h-20 opacity-70 text-amber-100" />
          </div>
          <div className="mt-3">
            <div className="font-semibold">{d.title}</div>
            {d.description && (
              <div className="text-xs mt-1 opacity-70 line-clamp-2">{d.description}</div>
            )}
            <div className="text-[10px] font-mono uppercase mt-2 opacity-60">
              {d.status === "ready"
                ? <>{d.page_count} pages · Ready</>
                : d.status === "processing"
                  ? <>Processing… {d.page_count || 0} of ~{d.page_count || "?"} pages</>
                  : d.status}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

